{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2024-2025                                 }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCDataGridDatabaseAdapter;

{$I WEBLib.TMSFNCDefines.inc}

{$IFDEF WEBLIB}
{$DEFINE SUPPORTCDS}
{$DEFINE SUPPORTCLONING}
{$ENDIF}

{$IFDEF FMXLIB}
{$DEFINE SUPPORTCDS}
{$DEFINE SUPPORTFIREDAC}
{$DEFINE SUPPORTABSTRACT}
{$ENDIF}
{$IFDEF VCLLIB}
{$DEFINE SUPPORTCDS}
{$DEFINE SUPPORTFIREDAC}
{$DEFINE SUPPORTABSTRACT}
{$ENDIF}

{$IFNDEF WEBLIB}
{$HINTS OFF}
{$IF COMPILERVERSION > 31}
{$DEFINE SUPPORTCLONING}
{$IFEND}
{$HINTS ON}
{$ENDIF}

interface

uses
  Classes, SysUtils, Generics.Collections, WEBLib.TMSFNCDataGrid, WEBLib.TMSFNCDataGridCore, WEBLib.TMSFNCDataGridBase, WEBLib.TMSFNCDataGridCell,
  WEBLib.TMSFNCDataGridData, WEBLib.TMSFNCDataGridRenderer, {%H-}WEBLib.TMSFNCTypes, WEBLib.Graphics, WEBLib.ExtCtrls
  {$IFDEF FMXLIB}
  ,FMX.Types
  {$ENDIF}
  {$IFDEF WEBLIB}
  ,DB
  {$ENDIF}
  {$IFNDEF WEBLIB}
  ,Data.DB
  {$ENDIF}
  ;

const
  MAJ_VER = 1; // Major version nr.
  MIN_VER = 0; // Minor version nr.
  REL_VER = 0; // Release nr.
  BLD_VER = 9; // Build nr.

  // version history
  // v1.0.0.0 : first release
  // v1.0.0.1 : Fixed: Issue removing columns index out of bounds
  //          : Improved: Refreshing dataset in combination with changed grid settings
  // v1.0.0.2 : Improved: Fallback mechanism on dataset when cloning is not available
  //          : Improved: LoadAllData to load all data in memory
  //          : Fixed: Issue with indexing, inserting & add columns at runtime
  //          : Fixed: Issue with field OnGetText & OnSetText
  // v1.0.0.3 : Fixed: Issue updating depending field values when editing
  //          : Fixed: Issue navigating columns without field connection
  // v1.0.0.4 : Fixed: Issue with lookup fields in combination with filtering & sorting
  //          : Improved : Filtering algorithm and combinations of AND / OR
  // v1.0.0.5 : Fixed: Issue with missing row data when using master-detail relations
  //          : Fixed: Issue with navigating the dataset in LoadMode := almAllRecords
  //          : Fixed: Index mapping on database adapter <> grid
  //          : Improved: property FieldNames, to add columns based on a specific fieldname
  // v1.0.0.6 : Improved: Visible property at grid column level now also used to hide columns
  // v1.0.0.7 : Fixed : Issue compiling TMS WEB Core with missing AutoCreated property
  // v1.0.0.8 : Fixed : Issue with updating cell data when grouping
  // v1.0.0.9 : Fixed : Issue with date-time field in TMS WEB Core

resourcestring
  sTMSFNCDataGridDatabaseAdapterUnidirectionalDataSet = 'Operation not allowed on a unidirectional dataset';
  sTMSFNCDataGridDatabaseAdapterNoDataGridAssigned = 'No Grid or Data Instance assigned';
  sTMSFNCDataGridDatabaseAdapterNoFieldsSpecified = 'No fields specified';
  sTMSFNCDataGridDatabaseAdapterCannotAddFieldsDataSource = 'Cannot add fields, no datasource assigned';
  sTMSFNCDataGridDatabaseAdapterCannotAddFieldsDataSet = 'Cannot add fields, no DataSet specified for datasource';
  sTMSFNCDataGridDatabaseAdapterCheckBoxField = 'Cannot assign true when CheckBoxField or ProgressField is also true';
  sTMSFNCDataGridDatabaseAdapterPictureField = 'Cannot assign true when PictureField or ProgressField is also true';
  sTMSFNCDataGridDatabaseAdapterCheckPictureField = 'Cannot assign true when CheckBoxField or PictureField is also true';

type
  TTMSFNCCustomDataGridDatabaseAdapter = class;

  TTMSFNCDataGridDatabaseAdapterDataLink = class(TDataLink)
  private
    FModified: Boolean;
    FAdapter: TTMSFNCCustomDataGridDatabaseAdapter;
    procedure SetModified(const Value: Boolean);
  protected
    procedure LayoutChanged; override;
    procedure ActiveChanged; override;
    procedure DataSetChanged; override;
    procedure DataSetScrolled(Distance: Integer); override;
    procedure RecordChanged(Field: TField); override;
    procedure UpdateData; override;
    function Adapter: TTMSFNCDataGridDataAdapter;
  public
    constructor Create(AAdapter: TTMSFNCCustomDataGridDatabaseAdapter);
    destructor Destroy; override;
    property Modified: Boolean read FModified write SetModified;
  end;

  TTMSFNCDataGridDatabaseAdapterColumn = class(TCollectionItem)
  private
    FAdapter: TTMSFNCCustomDataGridDatabaseAdapter;
    FAutoCreated: Boolean;
    FCheckBoxField: Boolean;
    FDataBoolean: Boolean;
    FDataInteger: NativeInt;
    FDataObject: TObject;
    FDataPointer: Pointer;
    FDataString: String;
    FField: TField;
    FFieldName: string;
    FHeader: string;
    FHTMLTemplate: string;
    FNew: Boolean;
    FPictureField: Boolean;
    FProgressField: Boolean;
    FUseColumnEditor: Boolean;
    FUseLookupEditor: boolean;
    function GetField: TField;
    function GetGridColumn: TTMSFNCDataGridColumn;
    procedure SetCheckBoxField(const Value: Boolean);
    procedure SetField(Value: TField);
    procedure SetFieldName(const Value: string);
    procedure SetHeader(const Value: string);
    procedure SetHTMLTemplate(const Value: string);
    procedure SetPictureField(const Value: Boolean);
    procedure SetProgressField(const Value: Boolean);
  protected
    function GetDisplayName: string; override;
    procedure DefineProperties(Filer : TFiler); override;
    procedure ReadAutoCreated(Reader: TReader);
    procedure SetIndex(Value: Integer); override;
    procedure WriteAutoCreated(Writer: TWriter);
  public
    constructor Create(ACollection: TCollection); override;
    destructor Destroy; override;
    function Adapter: TTMSFNCCustomDataGridDatabaseAdapter;
    procedure Assign(Source: TPersistent); override;
    property AutoCreated: Boolean read FAutoCreated write FAutoCreated;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataString: String read FDataString write FDataString;
    property Field: TField read GetField write SetField;
    property GridColumn: TTMSFNCDataGridColumn read GetGridColumn;
  published
    property CheckBoxField: Boolean read FCheckBoxField write SetCheckBoxField default false;
    property FieldName: string read FFieldName write SetFieldName;
    property Header: string read FHeader write SetHeader;
    property HTMLTemplate: string read FHTMLTemplate write SetHTMLTemplate;
    property PictureField: Boolean read FPictureField write SetPictureField default false;
    property ProgressField: Boolean read FProgressField write SetProgressField default false;
    property UseColumnEditor: Boolean read FUseColumnEditor write FUseColumnEditor default False;
    property UseLookupEditor: Boolean read FUseLookupEditor write FUseLookupEditor default True;
  end;

  {$IFDEF WEBLIB}
  TTMSFNCDataGridDatabaseAdapterColumns = class(TTMSFNCOwnedCollection)
  {$ENDIF}
  {$IFNDEF WEBLIB}
  TTMSFNCDataGridDatabaseAdapterColumns = class(TTMSFNCOwnedCollection<TTMSFNCDataGridDatabaseAdapterColumn>)
  {$ENDIF}
  private
    FAdapter: TTMSFNCCustomDataGridDatabaseAdapter;
    function GetItem(Index: Integer): TTMSFNCDataGridDatabaseAdapterColumn;
    procedure SetItem(Index: Integer; const Value: TTMSFNCDataGridDatabaseAdapterColumn);
  public
    constructor Create(AAdapter: TTMSFNCCustomDataGridDatabaseAdapter);
    function HasFieldsDefined: boolean;
    function Add: TTMSFNCDataGridDatabaseAdapterColumn;
    function Insert(index: Integer): TTMSFNCDataGridDatabaseAdapterColumn;
    function Adapter: TTMSFNCCustomDataGridDatabaseAdapter;
    property Items[Index: Integer]: TTMSFNCDataGridDatabaseAdapterColumn read GetItem write SetItem; default;
  end;

  TTMSFNCDataGridDatabaseAdapterDataSetType = (adsSequenced, adsNonSequenced);

  TTMSFNCDataGridDatabaseAdapterGetRecordCountEvent = procedure(Sender: TObject; var ARecordCount: Integer) of object;
  TTMSFNCDataGridDatabaseAdapterGetHTMLTemplateDataEvent = procedure(Sender: TObject; ACol, ARow: integer; AFieldname: string; var AData: string) of object;
  TTMSFNCDataGridDatabaseAdapterGetHTMLTemplateEvent = procedure(Sender: TObject; ACol, ARow: integer; var AHTMLTemplate: string; AFields: TFields) of object;

  TTMSFNCDataGridDatabaseAdapterLookupItem = class
  private
    FKey, FValue: TTMSFNCDataGridCellValue;
  public
    constructor Create(AKey, AValue: TTMSFNCDataGridCellValue);
    property Key: TTMSFNCDataGridCellValue read FKey write FKey;
    property Value: TTMSFNCDataGridCellValue read FValue write FValue;
  end;

  TTMSFNCDataGridDatabaseAdapterLoadMode = (almBuffered, almAllRecords);

  TTMSFNCDataGridDatabaseAdapterDataToField = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AMasterField, AField: TField; AInplaceEditor: TTMSFNCDataGridInplaceEditor; ACellValue: TTMSFNCDataGridCellValue; var Handled: Boolean) of object;
  TTMSFNCDataGridDatabaseAdapterFieldToData = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AMasterField, AField: TField; var ACellValue: TTMSFNCDataGridCellValue; var Handled: Boolean) of object;
  TTMSFNCDataGridDatabaseAdapterGetFilterValues = procedure(Sender: TObject; AColumn: Integer; AValues: TStrings) of object;
  TTMSFNCDataGridDatabaseAdapterBeforeApplyFilter = procedure(Sender: TObject; var AFilter: string) of object;
  TTMSFNCDataGridDatabaseAdapterAfterApplyFilter = procedure(Sender: TObject; AFilter: string) of object;

  TTMSFNCDataGridDatabaseAdapterLookupKeys = class(TDictionary<string, string>);

  TTMSFNCCustomDataGridDatabaseAdapter = class(TTMSFNCDataGridRendererDataAdapter)
  private
    FAutoCreateColumns: Boolean;
    FAutoRemoveColumns, FExpanded: Boolean;
    FColumns: TTMSFNCDataGridDatabaseAdapterColumns;
    FDataLink: TTMSFNCDataGridDatabaseAdapterDataLink;
    FDataSetType: TTMSFNCDataGridDatabaseAdapterDataSetType;
    FDataSetTypeAuto: Boolean;
    FDetailControl: TTMSFNCDataGridCellControl;
    FEditText: string;
    FInternalCall: Integer;
    FIsEditing: Boolean;
    FKeepData: Boolean;
    FLoadMode: TTMSFNCDataGridDatabaseAdapterLoadMode;
    FLookupKeys: TTMSFNCDataGridDatabaseAdapterLookupKeys;
    FOldDatasetType: TTMSFNCDataGridDatabaseAdapterDataSetType;
    FOldPosition: TBookMark;
    FOnAfterApplyFilter: TTMSFNCDataGridDatabaseAdapterAfterApplyFilter;
    FOnBeforeApplyFilter: TTMSFNCDataGridDatabaseAdapterBeforeApplyFilter;
    FOnDataToField: TTMSFNCDataGridDatabaseAdapterDataToField;
    FOnFieldToData: TTMSFNCDataGridDatabaseAdapterFieldToData;
    FOnFilterData: TNotifyEvent;
    FOnGetFilterValues: TTMSFNCDataGridDatabaseAdapterGetFilterValues;
    FOnGetHTMLTemplate: TTMSFNCDataGridDatabaseAdapterGetHTMLTemplateEvent;
    FOnGetHTMLTemplateData: TTMSFNCDataGridDatabaseAdapterGetHTMLTemplateDataEvent;
    FOnGetRecordCount: TTMSFNCDataGridDatabaseAdapterGetRecordCountEvent;
    FOnSortData: TNotifyEvent;
    FPrevCell: TTMSFNCDataGridCellCoord;
    FPrevTopRow: Integer;
    FShowBooleanFields: Boolean;
    FShowDateTimeFields: Boolean;
    FShowMemoFields: Boolean;
    FShowPictureFields: Boolean;
    function GetColumn(AFieldName: string): TTMSFNCDataGridDatabaseAdapterColumn;
    function GetDataSource: TDataSource;
    function GetFieldAtColumn(AColumn: Integer): TField;
    function GetFieldIndexAtColumn(AColumn: Integer): Integer;
    function GetFieldNames(AIndex: Integer): string;
    function GetLookupKeys: TTMSFNCDataGridDatabaseAdapterLookupKeys;
    procedure SetColumns(const Value: TTMSFNCDataGridDatabaseAdapterColumns);
    procedure SetDataSetType(const Value: TTMSFNCDataGridDatabaseAdapterDataSetType);
    procedure SetDataSource(const Value: TDataSource);
    procedure SetDetailControl(const Value: TTMSFNCDataGridCellControl);
    procedure SetFieldNames(AIndex: Integer; const Value: string);
    procedure SetLoadMode(const Value: TTMSFNCDataGridDatabaseAdapterLoadMode);
    procedure SetShowBooleanFields(const Value: Boolean);
    procedure SetShowDateTimeFields(const Value: Boolean);
    procedure SetShowMemoFields(const Value: Boolean);
    procedure SetShowPictureFields(const Value: Boolean);
  protected
    function CheckDatasetType: Boolean;
    function FilterMode: TTMSFNCDataGridDataAdapterFilterMode; override;
    function GetBufferCount: Integer; virtual;
    function GetCurrentRecord: Integer; virtual;
    function GetDocURL: string; override;
    function GetFieldNameForIndex(AColumn: Integer): string;
    function GetRecordCount: Integer; virtual;
    function GetVersion: string; override;
    function GroupMode: TTMSFNCDataGridDataAdapterGroupMode; override;
    function HTMLDBReplace(AValue: string; ADataset: TDataSet; ACol, ARow: Integer): string;
    function NeedsItemExtended(ACell: TTMSFNCDataGridCellCoord): Boolean; override;
    function SortMode: TTMSFNCDataGridDataAdapterSortMode; override;
    function GetColumnDataName(AColumn: Integer): string; override;
    function SyncTopRow: Integer; virtual;
    procedure MoveColumn(AFromColumn, AToColumn: Integer); override;
    procedure UpdateFilterBuilderColumns; override;
    procedure ActiveChange;
    procedure ApplyFilter; override;
    procedure BitmapChanged(Sender: TObject);
    procedure CanEditCell(ACell: TTMSFNCDataGridCellCoord; var ACanEdit: Boolean); override;
    procedure ClearFilter; override;
    procedure CollapseNodes;
    procedure DataChanged;
    procedure DataSetScrolled;
    procedure DoAfterApplyFilter(AFilter: string);
    procedure DoAutoRemoveColumns(ARemoveDefined: Boolean); virtual;
    procedure DoBeforeApplyFilter(var AFilter: string; var ACanApply: Boolean);
    procedure DoGetRecordCount(var ARecordCount: Integer); virtual;
    procedure ExportNotification(AState: TTMSFNCDataGridExportState; ARow: Integer); override;
    procedure GetCellData(ACell: TTMSFNCDataGridCellCoord; var ACellData: TTMSFNCDataGridCellValue); override;
    procedure GetCellDataProperties(ACell: TTMSFNCDataGridCellCoord; ACellItem: TTMSFNCDataGridCellItem); override;
    procedure GetCellFormatting(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; var AFormatting: TTMSFNCDataGridDataFormatting; var AConvertSettings: TFormatSettings; var ACanFormat: Boolean); override;
    procedure GetCellProperties(ACell: TTMSFNCDataGridCell); override;
    procedure GetFilterData(AColumn: Integer; AValues: TStrings); override;
    procedure GetInplaceEditorProperties(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; AInplaceEditorType: TTMSFNCDataGridInplaceEditorType); override;
    procedure GetInplaceEditorType(ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorType: TTMSFNCDataGridInplaceEditorType); override;
    procedure Initialize; override;
    procedure InternalConvertFieldToData(AField: TField; var AValue: TTMSFNCDataGridCellValue);
    procedure InternalDataChanged;
    procedure InternalSelectCell(ACell: TTMSFNCDataGridCellCoord);
    procedure InternalSyncPos(AUpdateTopRow: Boolean; AUpdateSelection: Boolean; ACollapse: Boolean);
    procedure LoadDetails;
    procedure LoadLookupList(AField: TField; AList: TStrings);
    procedure Notification(AComponent: TComponent; AOperation: TOperation); override;
    procedure RegisterRuntimeClasses; override;
    procedure RowAfterCollapse(ARow: Integer); override;
    procedure RowAfterExpand(ARow: Integer); override;
    procedure RowBeforeCollapse(ARow: Integer); override;
    procedure RowBeforeExpand(ARow: Integer); override;
    procedure RowCollapse(ARow: Integer); override;
    procedure RowExpand(ARow: Integer); override;
    procedure Scroll; override;
    procedure SetCellData(ACell: TTMSFNCDataGridCellCoord; ACellData: TTMSFNCDataGridCellValue); override;
    procedure SortData; override;
    procedure UpdateBounds; override;
    procedure UpdateData;
    procedure UpdateScrollMode; override;
    procedure UpdateVisibleFields;
    property AutoCreateColumns: Boolean read FAutoCreateColumns write FAutoCreateColumns default True;
    property AutoRemoveColumns: Boolean read FAutoRemoveColumns write FAutoRemoveColumns default True;
    property Columns: TTMSFNCDataGridDatabaseAdapterColumns read FColumns write SetColumns;
    property DataSetType: TTMSFNCDataGridDatabaseAdapterDataSetType read FDataSetType write SetDataSetType default adsSequenced;
    property DataSetTypeAuto: Boolean read FDataSetTypeAuto write FDataSetTypeAuto default True;
    property DataSource: TDataSource read GetDataSource write SetDataSource;
    property DetailControl: TTMSFNCDataGridCellControl read FDetailControl write SetDetailControl;
    property KeepData: Boolean read FKeepData write FKeepData default False;
    property LoadMode: TTMSFNCDataGridDatabaseAdapterLoadMode read FLoadMode write SetLoadMode default almBuffered;
    property LookupKeys: TTMSFNCDataGridDatabaseAdapterLookupKeys read GetLookupKeys;
    property OnAfterApplyFilter: TTMSFNCDataGridDatabaseAdapterAfterApplyFilter read FOnAfterApplyFilter write FOnAfterApplyFilter;
    property OnBeforeApplyFilter: TTMSFNCDataGridDatabaseAdapterBeforeApplyFilter read FOnBeforeApplyFilter write FOnBeforeApplyFilter;
    property OnDataToField: TTMSFNCDataGridDatabaseAdapterDataToField read FOnDataToField write FOnDataToField;
    property OnFieldToData: TTMSFNCDataGridDatabaseAdapterFieldToData read FOnFieldToData write FOnFieldToData;
    property OnFilterData: TNotifyEvent read FOnFilterData write FOnFilterData;
    property OnGetFilterValues: TTMSFNCDataGridDatabaseAdapterGetFilterValues read FOnGetFilterValues write FOnGetFilterValues;
    property OnGetHTMLTemplate: TTMSFNCDataGridDatabaseAdapterGetHTMLTemplateEvent read FOnGetHTMLTemplate write FOnGetHTMLTemplate;
    property OnGetHTMLTemplateData: TTMSFNCDataGridDatabaseAdapterGetHTMLTemplateDataEvent read FOnGetHTMLTemplateData write FOnGetHTMLTemplateData;
    property OnGetRecordCount: TTMSFNCDataGridDatabaseAdapterGetRecordCountEvent read FOnGetRecordCount write FOnGetRecordCount;
    property OnSortData: TNotifyEvent read FOnSortData write FOnSortData;
    property ShowBooleanFields: Boolean read FShowBooleanFields write SetShowBooleanFields default True;
    property ShowDateTimeFields: Boolean read FShowDateTimeFields write SetShowDateTimeFields default True;
    property ShowMemoFields: Boolean read FShowMemoFields write SetShowMemoFields default False;
    property ShowPictureFields: Boolean read FShowPictureFields write SetShowPictureFields default True;
    property Version: string read GetVersion;
  public
    class procedure BlobFieldToPicture(AField: TBlobField; ABitmap: TTMSFNCBitmap);
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    function CheckDataSet: Boolean;
    function GetActiveRow: Integer; virtual;
    function GetGridColumnIndex(AColumn: Integer): Integer;
    function IsValidDataCell(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsValidDataRow(ARow: Integer): Boolean;
    function SetActiveRecord(ARow: Integer): Boolean; virtual;
    procedure AddAllFields;
    procedure Assign(Source: TPersistent); override;
    procedure ConvertDataToField(ACell: TTMSFNCDataGridCellCoord; AMasterField, AField: TField; AInplaceEditor: TTMSFNCDataGridInplaceEditor; ACellValue: TTMSFNCDataGridCellValue);
    procedure ConvertFieldToData(ACell: TTMSFNCDataGridCellCoord; AMasterField, AField: TField; var ACellValue: TTMSFNCDataGridCellValue);
    procedure CreateColumns;
    procedure DeleteRow(ARow: Integer); override;
    procedure EditCell(ACell: TTMSFNCDataGridCellCoord); override;
    procedure EditSetData(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AValue: TTMSFNCDataGridCellValue); override;
    procedure InsertRow(ARow: Integer); override;
    procedure LoadAllData;
    procedure LoadValuesFromField(AField: TField; AValues: TStrings);
    procedure ReloadFromDataSet;
    procedure RemoveAllColumns;
    procedure RemoveAllFields;
    procedure SelectCell(ACell: TTMSFNCDataGridCellCoord); override;
    procedure Update; reintroduce;
    property ColumnAtField[AFieldName: string]: TTMSFNCDataGridDatabaseAdapterColumn read GetColumn;
    property DataLink: TTMSFNCDataGridDatabaseAdapterDataLink read FDataLink;
    property FieldAtColumn[AColumn: Integer]: TField read GetFieldAtColumn;
    property FieldIndexAtColumn[AColumn: Integer]: Integer read GetFieldIndexAtColumn;
    property FieldNames[AIndex: Integer]: string read GetFieldNames write SetFieldNames;
  end;

  [ComponentPlatformsAttribute(TMSPlatformsWEB)]
  TTMSFNCDataGridDatabaseAdapter = class(TTMSFNCCustomDataGridDatabaseAdapter)
  protected
    property DataSetType;
    property DataSetTypeAuto;
  public
    constructor Create(AOwner: TComponent); overload; override;
    constructor Create(AOwner: TComponent; ASkipAssign: Boolean); reintroduce; overload; virtual;
  published
    property AutoCreateColumns;
    property AutoRemoveColumns;
    property Columns;
    property DataSource;
    property DetailControl;
    property KeepData;
    property LoadMode;
    property OnAfterApplyFilter;
    property OnBeforeApplyFilter;
    property OnDataToField;
    property OnFieldToData;
    property OnFilterData;
    property OnGetFilterValues;
    property OnGetHTMLTemplate;
    property OnGetHTMLTemplateData;
    property OnGetRecordCount;
    property OnSortData;
    property ShowBooleanFields;
    property ShowDateTimeFields;
    property ShowMemoFields;
    property ShowPictureFields;
    property Version;
  end;

implementation

uses
  WEBLib.Forms, TMS.TMSFNCFilterBuilder, WEBLib.TMSFNCGraphics, WEBLib.TMSFNCScrollBar, WEBLib.StdCtrls, Rtti, WEBLib.TMSFNCGraphicsTypes, WEBLib.TMSFNCUtils, Math
  {$IFDEF FMXLIB}
  , FMX.Edit, FMX.ListBox, FMX.ComboEdit
  {$ENDIF}
  {$IFDEF SUPPORTABSTRACT}
  ,TMS.TMSFNCDataSet
  {$ENDIF}
  {$IFDEF SUPPORTCDS}
  {$IFDEF WEBLIB}
  ,WEBLib.CDS
  {$ELSE}
  ,Datasnap.DBClient
  {$ENDIF}
  {$ENDIF}
  {$IFDEF SUPPORTFIREDAC}
  ,FireDAC.Comp.DataSet
  ,FireDAC.Stan.Intf
  {$ENDIF}
  {$HINTS OFF}
  {$IF COMPILERVERSION > 22}
  ,UITypes
  {$IFEND}
  {$HINTS ON}
  ;

{$R TMSFNCDataGridDatabaseAdapter.res}

type
  TTMSFNCCustomDataGridOpen = class(TTMSFNCCustomDataGrid);
  TTMSFNCDataGridRendererOpen = class(TTMSFNCDataGridRenderer);
  TCustomComboBoxOpen = class(TCustomComboBox);
  TCustomEditOpen = class(TCustomEdit);
  {$IFDEF SUPPORTCDS}
  TCustomClientDataSetOpen = class(TCustomClientDataSet);
  {$ENDIF}

function TextWidthToColWidth(const AColumn: Integer; const ADataInstance: TTMSFNCDataGridData; const ATextLength: Integer): Single;
var
  g: TTMSFNCGraphics;
  r: TTMSFNCDataGridRenderer;
  l: TTMSFNCDataGridCellLayout;

  function GetDefaultCalc: Single;
  begin
    Result := 7 * ATextLength + 4;
  end;
begin
  if Assigned(ADataInstance) and (ADataInstance is TTMSFNCDataGridRenderer)  then
  begin
    r := ADataInstance as TTMSFNCDataGridRenderer;
    l := TTMSFNCDataGridRendererOpen(r).GetCellAppearance(gcsNormal, MakeCell(AColumn, 0));
    if Assigned(l) then
    begin
      g := TTMSFNCGraphics.CreateBitmapCanvas;
      try
        g.Font.Assign(l.Font);
        Result := g.CalculateTextWidth(StringOfChar('0', ATextLength)) + 4;
      finally
        g.Free;
      end;
    end
    else
      Result := GetDefaultCalc;
  end
  else
    Result := GetDefaultCalc;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetFieldNameForIndex(AColumn: Integer): string;
begin
  Result := '';
  if (AColumn >= 0) and (AColumn <= DataLink.DataSet.FieldCount - 1) then
    Result := DataLink.DataSet.Fields[AColumn].FieldName;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.ActiveChange;
var
  I, c: Integer;
  f: TField;
  rc: Integer;
  fv: Boolean;
  fn: string;
  gc: TTMSFNCDataGridColumn;
  cl: TTMSFNCDataGridDatabaseAdapterColumn;

  procedure ClearData;
  var
    K: Integer;
  begin
    DataInstance.BeginUpdate;
    for K := DataInstance.FixedRowCount to DataInstance.RowCount - 1 do
    begin
      DataInstance.ClearDataForRow(K);
      DataInstance.ClearRowInfo(K);
    end;
    DataInstance.RowCount := DataInstance.FixedRowCount + DataInstance.FixedBottomRowCount;
    DoAutoRemoveColumns(not (Assigned(FDataLink) and Assigned(DataLink.DataSet)));
    DataInstance.EndUpdate;
  end;

begin
  if (FInternalCall > 0) or IsDestroying then
    Exit;

  Inc(FInternalCall);

  if Assigned(FDataLink) and Assigned(DataLink.DataSet) then
  begin
    if DataLink.DataSet.Active and Active then
    begin
      if FAutoCreateColumns then
      begin
        for i := 0 to DataLink.DataSet.FieldCount - 1 do
        begin
          fn := DataLink.DataSet.Fields[i].FieldName;
          cl := ColumnAtField[fn];
          if not Assigned(cl) then
          begin
            cl := Columns.Add;
            cl.AutoCreated := True;
            cl.FNew := True;
            cl.FieldName := GetFieldNameForIndex(I);
          end;
        end;
      end;

      CheckDatasetType;

      rc := GetRecordCount;
      if Assigned(DataInstance) then
      begin
        DataInstance.BeginUpdate;
        DataInstance.ClearData;
        DataInstance.ClearRowInfo;
        DataInstance.ColumnCount := Columns.Count + DataInstance.FixedColumnCount + DataInstance.FixedRightColumnCount;
        DataInstance.RowCount := rc + DataInstance.FixedRowCount + DataInstance.FixedBottomRowCount;

        for i := 0 to Columns.Count - 1 do
        begin
          f := FieldAtColumn[i];

          if Assigned(f) then
          begin
            fv := not f.Visible;

            gc := Columns[i].GridColumn;
            if Assigned(gc) then
            begin
              fv := fv or not gc.Visible;
              c := gc.Index;
            end
            else
              c := i;

            if fv then
              DataInstance.HideColumn(c)
            else
              DataInstance.UnHideColumn(c);

            if Assigned(gc) then
            begin
              if (f.DisplayWidth > 0) and Columns[i].FNew then
              begin
                gc.Width := TextWidthToColWidth(gc.Index, DataInstance, f.DisplayWidth);
                Columns[i].FNew := False;
              end;
            end;
          end;
        end;

        DataInstance.EndUpdate;

        DataLink.BufferCount := GetBufferCount;

        LoadDetails;

        InternalSyncPos(True, True, True);
        DataInstance.RefreshData;
      end;
    end
    else if not KeepData and Assigned(DataInstance) then
      ClearData;

    if Assigned(Renderer) then
    begin
      FPrevTopRow := Renderer.TopRow;
      FPrevCell := Renderer.FocusedCell;
    end;
  end
  else if not KeepData and Assigned(DataInstance) then
    ClearData;

  Dec(FInternalCall);
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.AddAllFields;
var
  sl: TStringList;
  i, j: Integer;
begin
  if not Assigned(DataSource) then
  begin
    TTMSFNCUtils.Message(sTMSFNCDataGridDatabaseAdapterCannotAddFieldsDataSource);
    Exit;
  end;

  if not Assigned(DataSource.DataSet) then
  begin
    TTMSFNCUtils.Message(sTMSFNCDataGridDatabaseAdapterCannotAddFieldsDataSet);
    Exit;
  end;

  sl := TStringList.Create;
  try
    DataSource.DataSet.GetFieldNames(sl);
    j := 0;

    if (sl.Count > 0) and (sl.Count + j < Columns.Count) then
    begin
      while (sl.Count + j < Columns.Count) and (Columns.Count > j) do
      begin
        Columns.Delete(Columns.Count - 1);
      end;
    end;

    for i := 1 to sl.Count do
    begin
      if Columns.Count > i - 1 + j then
      begin
        Columns[i - 1 + j].FieldName := sl[i - 1];
        Columns[i - 1 + j].Header := sl[i - 1];
      end
      else
      begin
        with Columns.Add do
        begin
          FieldName := sl[i - 1];
          Header := sl[i - 1];
        end;
      end;
    end;
  finally
    sl.Free;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.UpdateFilterBuilderColumns;
var
  I: Integer;
  dt: TTMSFNCFilterBuilderDataType;
  cp: TTMSFNCFilterBuilderColumnProperties;
begin
  if Assigned(FDataLink) and Assigned(DataLink.DataSet) and Assigned(DataInstance) then
  begin
    for I := 0 to DataLink.DataSet.Fields.Count - 1 do
    begin
      if DataLink.DataSet.Fields[I].FieldKind = fkData then
      begin
        case DataLink.DataSet.Fields[I].DataType of
          ftWord, ftInteger, ftAutoInc, ftLargeInt: dt := fdtNumber;
          ftFloat: dt := fdtFloat;
          ftDateTime, ftTimeStamp, ftTimeStampOffset: dt := fdtDateTime;
          ftDate: dt := fdtDate;
          ftTime: dt := fdtTime;
          ftBoolean: dt := fdtBoolean;
          ftString, ftWideString, ftFixedChar, ftMemo, ftWideMemo: dt := fdtText;
          ftBlob, {ftMemo,} ftGraphic: dt := fdtOther;
          else
            dt := fdtAutomatic;
        end;

        cp.Name := DataLink.DataSet.Fields[I].FieldName;
        cp.DataType := dt;
        DataInstance.FilterBuilder.AddDataColumn(cp.Name, cp.DataType, cp.DisplayName);
      end;
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.ApplyFilter;
var
  c: Integer;
  f: TField;
  flt, fltx, cnd: string;
  fd, fdn, fde: TTMSFNCDataGridDataFilterData;
  fld: TTMSFNCDataGridDataFilter;
  I: Integer;
  cd: string;
  b: Boolean;
  i64: Int64;
  d: Double;
  s, sOr, sAnd, fn, fnf: string;
  J: Integer;
  a: Boolean;

  function LookupValue(AValue: string): string;
  var
    b: string;
    v: string;
  begin
    Result := AValue;
    b := Result;

    if not (f.FieldKind = fkLookup) then
      Exit;

    if LookupKeys.TryGetValue(b, v) then
      Result := StringReplace(Result, b, v, [rfReplaceAll]);
  end;
begin
  if CheckDataSet and Assigned(DataLink) then
  begin
    Inc(FInternalCall);
    DataLink.DataSet.DisableControls;

    try
      if Assigned(OnFilterData) then
        OnFilterData(Self)
      else
      begin
        sOr := 'OR';
        sAnd := 'AND';

        DataLink.DataSet.Filtered := False;
        if not DataInstance.Options.Filtering.Advanced then
        begin
          fltx := '';
          for I := 0 to DataInstance.Filter.Count - 1 do
          begin
            fd := DataInstance.Filter.Items[i];
            if fd.Enabled then
            begin
              fld := TTMSFNCDataGridDataFilter.Create(nil, TTMSFNCDataGridDataFilterData);
              try
                if fd.&Type in [gftEmpty, gftNotEmpty] then
                  fld.Add.Assign(fd)
                else
                  fld.Parse(fd.Column, fd.FilterCondition);

                flt := '(';

                for J := 0 to fld.Count - 1 do
                begin
                  fde := fld.Items[J];

                  c := fde.Column - DataInstance.FixedColumnCount;
                  f := FieldAtColumn[c];
                  if Assigned(f) then
                  begin
                    cd := fde.Condition;
                    if (cd <> '') or (fde.&Type in [gftEmpty, gftNotEmpty]) then
                    begin
                      b := True;
                      if not (f.FieldKind = fkLookup) then
                      begin
                        case f.DataType of
                          ftInteger, ftLargeint, ftSmallInt, ftWord: b := TryStrToInt64(cd, i64);
                          ftFloat, ftCurrency: b := TryStrToFloat(cd, d);
                        end;
                      end;

                      cnd := '';

                      if b then
                      begin
                        if f.FieldKind = fkLookup then
                          fn := f.KeyFields
                        else
                          fn := f.FieldName;

                        fnf := '[' + fn + ']';

                        cd := LookupValue(cd);

                        case fde.&Type of
                          gftEmpty: s := fnf + ' = NULL';
                          gftNotEmpty: s := 'NOT ' + fnf + ' = NULL';
                          gftStartsWith: s := fnf + ' LIKE ' + QuotedStr(cd + '%');
                          gftEndsWith: s := fnf + ' LIKE ' + QuotedStr('%' + cd);
                          gftContains: s := fnf + ' LIKE ' + QuotedStr('%' + cd + '%');
                          gftNotContains: s := 'NOT ' + fnf + ' LIKE ' + QuotedStr('%' + cd + '%');
                          gftEqual: s := fnf + ' = ' + QuotedStr(cd);
                          gftNotEqual: s := fnf + ' <> ' + QuotedStr(cd);
                          gftLargerThan: s := fnf + ' > ' + QuotedStr(cd);
                          gftSmallerThan: s := fnf + ' < ' + QuotedStr(cd);
                          gftLargerOrEqualThan: s := fnf + ' >= ' + QuotedStr(cd);
                          gftSmallerOrEqualThan: s := fnf + ' <= ' + QuotedStr(cd);
                        end;

                        cnd := s;
                      end;

                      if (flt <> '') and (J > 0) and (cnd <> '') then
                      begin
                        fdn := fld.Items[J];
                        case fdn.Operation of
                          gfoSHORT, gfoNONE, gfoXOR: ;
                          gfoAND: flt := flt + ' ' + sAnd + ' ' + cnd;
                          gfoOR: flt := flt + ' ' + sOr + ' ' + cnd;
                        end;
                      end
                      else
                        flt := flt + cnd;
                    end;
                  end;
                end;
              finally
                fld.free;
              end;

              flt := flt + ')';

              if flt = '()' then
                flt := '';

              if (fltx <> '') and (I > 0) and (flt <> '') then
              begin
                fdn := DataInstance.Filter.Items[I];
                case fdn.Operation of
                  gfoSHORT, gfoNONE, gfoXOR: ;
                  gfoAND: fltx := fltx + ' ' + sAnd + ' ' + flt;
                  gfoOR: fltx := fltx + ' ' + sOr + ' ' + flt;
                end;
              end
              else
                fltx := fltx + flt;
            end;
          end;
        end
        else
          fltx := DataInstance.FilterBuilder.FilterText;

        if fltx <> '' then
        begin
          a := True;
          DoBeforeApplyFilter(fltx, a);
          if a then
          begin
            DataLink.DataSet.Filter := fltx;
            DataLink.DataSet.Filtered := True;
            DoAfterApplyFilter(fltx);
          end;
        end;
      end;
    finally
      InternalDataChanged;
      DataLink.DataSet.EnableControls;
      Dec(FInternalCall);
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCCustomDataGridDatabaseAdapter then
  begin
    FColumns.Assign((Source as TTMSFNCCustomDataGridDatabaseAdapter).Columns);
    FAutoRemoveColumns := (Source as TTMSFNCCustomDataGridDatabaseAdapter).AutoRemoveColumns;
    FAutoCreateColumns := (Source as TTMSFNCCustomDataGridDatabaseAdapter).AutoCreateColumns;
    FDataSetType := (Source as TTMSFNCCustomDataGridDatabaseAdapter).DataSetType;
    FDataSetTypeAuto := (Source as TTMSFNCCustomDataGridDatabaseAdapter).DataSetTypeAuto;
    FShowBooleanFields := (Source as TTMSFNCCustomDataGridDatabaseAdapter).ShowBooleanFields;
    FShowPictureFields := (Source as TTMSFNCCustomDataGridDatabaseAdapter).ShowPictureFields;
    FShowDateTimeFields := (Source as TTMSFNCCustomDataGridDatabaseAdapter).ShowDateTimeFields;
    FShowMemoFields := (Source as TTMSFNCCustomDataGridDatabaseAdapter).ShowMemoFields;
    FLoadMode := (Source as TTMSFNCCustomDataGridDatabaseAdapter).LoadMode;
    FKeepData := (Source as TTMSFNCCustomDataGridDatabaseAdapter).KeepData;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.BitmapChanged(Sender: TObject);
begin
  //DataGrid.Invalidate;
end;

class procedure TTMSFNCCustomDataGridDatabaseAdapter.BlobFieldToPicture(AField: TBlobField; ABitmap: TTMSFNCBitmap);
{$IFNDEF WEBLIB}
type
  TGraphicHeader = record
    Count: Word;                { Fixed at 1 }
    HType: Word;                { Fixed at $0100 }
    Size: Integer              { Size not including header }
  end;
var
  s: TStream;
  Size: Longint;
  GraphicHeader: TGraphicHeader;
  Header: TBytes;

  {$IFDEF CMNLIB}
  ms: TMemoryStream;
  gcc: TGraphicClass;
  p: TGraphic;
  {$ENDIF}

  sig: word;
  i: Integer;
  oletype: Integer;
  oleoffset: Integer;
  b: Byte;
{$ENDIF}
begin
  if not Assigned(AField) then
    Exit;

  if not Assigned(AField.DataSet) then
    Exit;

  {$IFDEF WEBLIB}
  ABitmap.LoadFromURL(AField.AsString);
  {$ELSE}
  s := AField.DataSet.CreateBlobStream(AField, bmRead);
  if Assigned(s) then
  begin
    try
      Size := s.Size;
      if Size >= SizeOf(TGraphicHeader) then
      begin
        SetLength(Header, SizeOf(TGraphicHeader));
        s.Read(Header, 0, Length(Header));
        System.Move(Header[0], GraphicHeader, SizeOf(TGraphicHeader));
        if (GraphicHeader.Count <> 1) or (GraphicHeader.HType <> $0100) or
          (GraphicHeader.Size <> Size - SizeOf(GraphicHeader)) then
          s.Position := 0;
      end;

      if (s.Position = 0) and (s.Size > 2) then
      begin
        oletype := -1;
        oleoffset := 0;

        sig := 0;
        s.Read(sig, 2);
        case sig of
          $1C15:
          begin
            i := 0;
            b := 0;
            while (i < 512) do
            begin
              s.Read(b, 1);
              inc(i);
              if (b = $FF) then
              begin
                s.Read(b, 1);
                inc(i);
                if b = $D8 then
                begin
                  oletype := 1;
                  oleoffset := i;
                  break;
                end;
              end;
              if (b = $47) then
              begin
                s.Read(b, 1);
                inc(i);
                if b = $49 then
                begin
                  oletype := 2;
                  oleoffset := i;
                  break;
                end;
              end;
              if (b = ord('B')) then
              begin
                s.Read(b, 1);
                inc(i);
                if (b = ord('M')) then
                begin
                  oletype := 0;
                  oleoffset := i;
                  Break;
                end;
              end;
            end;
          end;
        end;

        if oletype in [0, 1, 2] then
          s.Seek(oleoffset, TSeekOrigin.soBeginning)
        else
          s.Position := 0;
      end;

      {$IFDEF CMNLIB}
      ms := TMemoryStream.Create;
      ms.CopyFrom(s, s.Size - s.Position);
      ms.Position := 0;

      try
        if TTMSFNCUtils.FindGraphicClass(ms.Memory^, ms.Size, gcc) then
        begin
          p := gcc.Create;
          try
            ms.Position := 0;
            p.LoadFromStream(ms);
            ABitmap.Assign(p)
          finally
            p.Free;
          end;
        end;
      finally
        ms.Free;
      end;
      {$ENDIF}
      {$IFDEF FMXLIB}
      ABitmap.LoadFromStream(s);
      {$ENDIF}
    finally
      s.Free;
    end;
  end;
  {$ENDIF}
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.Update;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  CollapseNodes;
  DataLink.BufferCount := GetBufferCount;
  InternalSyncPos(True, True, False);
  DataInstance.RefreshData;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.UpdateBounds;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  if (FInternalCall > 0) or (LoadMode = almAllRecords) then
    Exit;

  Inc(FInternalCall);
  try
    CollapseNodes;
    DataLink.BufferCount := GetBufferCount;
    InternalSyncPos(True, True, False);
    DataInstance.RefreshData(False);
  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.UpdateData;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  if FInternalCall > 0 then
    Exit;

  DataInstance.RefreshData(True);
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.UpdateScrollMode;
begin
  DataChanged;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.CanEditCell(
  ACell: TTMSFNCDataGridCellCoord; var ACanEdit: Boolean);
var
  c: Integer;
  f: TField;
  vd: Boolean;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  c := ACell.Column - DataInstance.FixedColumnCount;

  vd := IsValidDataCell(MakeCell(ACell.Column, ACell.Row));
  if not vd then
    Exit;

  if Assigned(FDataLink) then
  begin
    if FDataLink.Active and Assigned(FDataLink.DataSet) then
      if FDataLink.DataSet.Active and not FDataLink.DataSet.CanModify then
      begin
        if (c < Columns.Count) and (Columns[c].FieldName <> '') then
        begin
          ACanEdit := False;
          Exit;
        end;
      end;
  end;

  if (c >= Columns.Count) then
  begin
    ACanEdit := True;
    Exit;
  end;

  if (c >= 0) and (c <= Columns.Count - 1) and (Columns[c].HTMLTemplate <> '') then
  begin
    ACanEdit := False;
    Exit;
  end;

  if DataInstance.IsGrouped then
    ACanEdit := not DataInstance.IsRowNode(ACell.Row);

  if ACanEdit then
  begin
    f := FieldAtColumn[c];
    if Assigned(f) then
      ACanEdit := not f.ReadOnly and (not (f.DataType in ftNonTextTypes) or Assigned(f.OnSetText));

    if ACanEdit then
    begin
      if ShowBooleanFields and Assigned(f) and (f.DataType = ftBoolean) then
        ACanEdit := False;

      if (Columns[c].CheckBoxField) then
        ACanEdit := False;

      if ShowPictureFields and Assigned(f) and (f.DataType = ftGraphic) then
        ACanEdit := False;

      if (Columns[c].PictureField) then
        ACanEdit := False;
    end;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.CheckDataSet: Boolean;
begin
  Result := Active and Assigned(FDataLink) and Assigned(DataLink.DataSet) and DataLink.DataSet.Active;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.CheckDatasetType: Boolean;
begin
  if DataSetTypeAuto then
  begin
    if not FDataLink.DataSet.IsSequenced then
      FDataSetType := adsNonSequenced
    else
      FDataSetType := adsSequenced;
  end;

  Result := (FOldDatasetType <> FDataSetType) and (DataLink.DataSet.State = dsBrowse);
  FOldDatasetType := FDataSetType;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.ClearFilter;
begin
  if CheckDataSet and Assigned(DataLink) then
  begin
    Inc(FInternalCall);
    DataLink.DataSet.DisableControls;

    try
      DataLink.DataSet.Filtered := False;
      DataLink.DataSet.Filter := '';
    finally
      InternalDataChanged;
      DataLink.DataSet.EnableControls;
      Dec(FInternalCall);
    end;
  end;
end;

constructor TTMSFNCCustomDataGridDatabaseAdapter.Create(AOwner: TComponent);
begin
  inherited;
  FExpanded := False;
  FLoadMode := almBuffered;
  FKeepData := False;
  FPrevTopRow := -1;
  FEditText := '';
  FDataSetTypeAuto := True;
  FDataSetType := adsSequenced;
  FDataLink := TTMSFNCDataGridDatabaseAdapterDataLink.Create(Self);
  FColumns := TTMSFNCDataGridDatabaseAdapterColumns.Create(Self);
  FAutoRemoveColumns := True;
  FAutoCreateColumns := True;
  FShowPictureFields := True;
  FShowDateTimeFields := True;
  FShowBooleanFields := True;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.CreateColumns;
var
  I: Integer;
begin
  if (FInternalCall > 0) or IsDestroying then
    Exit;

  Inc(FInternalCall);

  Columns.Clear;

  if Assigned(FDataLink) and Assigned(DataLink.DataSet) then
  begin
    if DataLink.DataSet.Active and Active then
    begin
      for i := 0 to DataLink.DataSet.FieldCount - 1 do
      begin
        if DataLink.DataSet.Fields[i].Visible then
          Columns.Add;
      end;

      for i := 0 to Columns.Count - 1 do
      begin
        Columns[i].AutoCreated := True;
        Columns[i].FNew := True;
        Columns[i].FieldName := GetFieldNameForIndex(I);
      end;
    end
  end;

  Dec(FInternalCall);
  Update;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.DataChanged;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  if FInternalCall > 0 then
    Exit;

  Inc(FInternalCall);
  try
    InternalDataChanged;
  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.DataSetScrolled;
begin
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.DeleteRow(ARow: Integer);
var
  vd: Boolean;
begin
  if not CheckDataSet or not Assigned(Renderer) then
    Exit;

  vd := IsValidDataRow(ARow);
  if not vd then
    Exit;

  Inc(FInternalCall);

  try
    if not (FDataLink.DataSet.State in [dsEdit, dsInsert]) then
    begin
      InternalSelectCell(MakeCell(Renderer.FocusedCell.Column, ARow));
      FDataLink.DataSet.Delete;
      InternalDataChanged;
    end;
  finally
    Dec(FInternalCall);
  end;
end;

destructor TTMSFNCCustomDataGridDatabaseAdapter.Destroy;
begin
  FreeAndNil(FLookupKeys);
  FreeAndNil(FColumns);
  FreeAndNil(FDataLink);
  inherited;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.DoAfterApplyFilter(
  AFilter: string);
begin
  if Assigned(OnAfterApplyFilter) then
    OnAfterApplyFilter(Self, AFilter);
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.DoAutoRemoveColumns(
  ARemoveDefined: Boolean);
var
  DoUpdate,CanRemove: Boolean;
  i: integer;
begin
  if FAutoRemoveColumns then
  begin
    DoUpdate := true;

    CanRemove := (not Columns.HasFieldsDefined or ARemoveDefined);

//    if (csDesigning in ComponentState) and not (csLoading in ComponentState) and not (csReading in ComponentState) and CanRemove then
//      DoUpdate := TTMSFNCUtils.&Message('Automatically remove columns ?', TMsgDlgType. mtConfirmation, [TMsgDlgBtn.mbYes, TMsgDlgBtn.mbNo],0) = mrYes;

    if (csLoading in ComponentState) then
      DoUpdate := false;

    for i := 0 to Columns.Count - 1 do
      Columns[i].FField := nil;

    if CanRemove and DoUpdate then
    begin
      Columns.Clear;
      if Assigned(DataInstance) then
        DataInstance.ColumnCount := 0;
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.DoBeforeApplyFilter(
  var AFilter: string; var ACanApply: Boolean);
begin
  if Assigned(OnBeforeApplyFilter) then
    OnBeforeApplyFilter(Self, AFilter);
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.DoGetRecordCount(
  var ARecordCount: Integer);
begin
  if Assigned(OnGetRecordCount) then
    OnGetRecordCount(Self, ARecordCount);
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.EditCell(
  ACell: TTMSFNCDataGridCellCoord);
var
  c: Integer;
  f: TField;
  vd: Boolean;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  vd := IsValidDataCell(MakeCell(ACell.Column, ACell.Row));
  if not vd then
    Exit;

  Inc(FInternalCall);
  try
    c := ACell.Column - DataInstance.FixedColumnCount;

    f := FieldAtColumn[c];

    InternalSelectCell(ACell);

    if not FDatalink.Readonly and Assigned(f) and f.CanModify and (not (f.DataType in ftNonTextTypes) or Assigned(f.OnSetText)) then
    begin
      if not FDataLink.Editing then
        FDatalink.Edit;

      if FDataLink.Editing then
        FDatalink.Modified := True;
    end;

  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.CollapseNodes;
begin
  if Assigned(DetailControl) and Assigned(Renderer) and FExpanded then
    Renderer.CollapseAllNodes;

  FExpanded := False;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.ConvertDataToField(ACell: TTMSFNCDataGridCellCoord; AMasterField, AField: TField; AInplaceEditor: TTMSFNCDataGridInplaceEditor; ACellValue: TTMSFNCDataGridCellValue);
var
  idx: Integer;
  it: TStrings;
  li: TTMSFNCDataGridDatabaseAdapterLookupItem;
  o: TObject;
  h: Boolean;
begin
  if Assigned(AField) then
  begin
    h := False;
    if Assigned(OnDataToField) then
      OnDataToField(Self, ACell, AMasterField, AField, AInplaceEditor, ACellValue, h);

    if not h then
    begin
      if Assigned(AInplaceEditor) then
      begin
        it := nil;
        idx := -1;
        if (AInplaceEditor is TCustomComboBox) then
        begin
          idx := TCustomComboBoxOpen(AInplaceEditor as TCustomComboBox).ItemIndex;
          it := TCustomComboBoxOpen(AInplaceEditor as TCustomComboBox).Items;
        end
        {$IFDEF FMXLIB}
        else if AInplaceEditor is TCustomComboEdit then
        begin
          idx := (AInplaceEditor as TCustomComboEdit).ItemIndex;
          it := (AInplaceEditor as TCustomComboEdit).Items;
        end
        {$ENDIF}
        ;

        if Assigned(it) and (idx >= 0) and (idx <= it.Count - 1) then
        begin
          o := it.Objects[idx];
          if Assigned(o) and (o is TTMSFNCDataGridDatabaseAdapterLookupItem) then
          begin
            li := o as TTMSFNCDataGridDatabaseAdapterLookupItem;
            ACellValue := TTMSFNCDataGridData.ValueToString(li.Key);
          end;
        end;
      end;

      if Assigned(AField.OnSetText) then
        AField.OnSetText(AField, TTMSFNCDataGridData.ValueToString(ACellValue))
      else
      begin
        {$IFDEF WEBLIB}
        case AField.DataType of
          ftWord, ftInteger, ftAutoInc: AField.AsInteger := TTMSFNCDataGridData.ValueToInteger(ACellValue, FormatSettings);
          ftLargeInt: AField.AsLargeInt := TTMSFNCDataGridData.ValueToInt64(ACellValue, FormatSettings);
          ftFloat: AField.AsFloat := TTMSFNCDataGridData.ValueToFloat(ACellValue, FormatSettings);
          ftDateTime, ftDate, ftTime: AField.AsDateTime := TTMSFNCDataGridData.ValueToDateTime(ACellValue, FormatSettings);
          ftBoolean: AField.AsBoolean := TTMSFNCDataGridData.ValueToBoolean(ACellValue, FormatSettings);
          ftString, ftWideString, ftFixedChar, ftMemo, ftWideMemo: AField.AsString := TTMSFNCDataGridData.ValueToString(ACellValue);
          ftVariant: AField.AsJSValue := ACellValue;
        end;
        {$ELSE}
        if AField.DataType in [ftSmallint, ftShortInt, ftByte, ftWord, ftInteger, ftBCD, ftLongWord,
          ftSingle, ftFloat, ftDateTime, ftBoolean, ftCurrency, ftDate, ftTime, ftString,
          ftWideString, ftLargeInt, ftAutoInc, ftFixedChar, ftVariant, ftExtended, ftSingle, ftMemo, ftWideMemo] then
        begin
          if Assigned(AField.OnSetText) then
            AField.OnSetText(AField, DataInstance.ValueToString(ACellValue))
          else
            AField.AsVariant := ACellValue.AsVariant
        end
        else if AField.DataType = ftTimeStamp then
          AField.AsDateTime := TTMSFNCDataGridData.ValueToDateTime(ACellValue, FormatSettings);
        {$ENDIF}
      end;
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.ConvertFieldToData(ACell: TTMSFNCDataGridCellCoord; AMasterField, AField: TField; var ACellValue: TTMSFNCDataGridCellValue);
var
  h: Boolean;
begin
  if Assigned(AField) then
  begin
    h := False;
    if Assigned(OnFieldToData) then
      OnFieldToData(Self, ACell, AMasterField, AField, ACellValue, h);

    if not h then
      InternalConvertFieldtoData(AField, ACellValue);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.EditSetData(
  ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AValue: TTMSFNCDataGridCellValue);
var
  c, cl: Integer;
  m, f: TField;
  vd: Boolean;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  vd := IsValidDataCell(MakeCell(ACell.Column, ACell.Row));
  if not vd then
    Exit;

  Inc(FInternalCall);
  try
    cl := ACell.Column;
    c := cl - DataInstance.FixedColumnCount;

    f := FieldAtColumn[c];
    m := nil;
    if Assigned(f) and (f.FieldKind = fkLookup) then
    begin
      m := f;
      f := f.Dataset.FieldByName(f.KeyFields);
    end;

    if Assigned(f) then
    begin
      InternalSelectCell(ACell);

      if not FDatalink.Readonly and Assigned(f) and f.CanModify and (not (f.DataType in ftNonTextTypes) or Assigned(f.OnSetText)) then
      begin
        if not FDataLink.Editing then
          FDatalink.Edit;

        if FDataLink.Editing then
          FDatalink.Modified := True;

        if Assigned(Renderer) and (cl >= 0) and (cl <= Renderer.Columns.Count - 1) and (c <= Columns.Count - 1) and Columns[c].CheckBoxField then
        begin
          if DataInstance.ValueToBoolean(AValue, FormatSettings) then
            AValue := Renderer.Columns[cl].Binding.BooleanTrueValue
          else
            AValue := Renderer.Columns[cl].Binding.BooleanFalseValue;
        end;

        ConvertDataToField(ACell, m, f, AInplaceEditor, AValue);
        Renderer.RefreshData;

        FIsEditing := True;
      end;
    end;
  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.ExportNotification(
  AState: TTMSFNCDataGridExportState; ARow: Integer);

  procedure ClearOldPosition;
  begin
    {$IFDEF WEBLIB}
    FOldPosition.Data := nil;
    FOldPosition.Flag := bfCurrent;
    {$ENDIF}
    {$IFNDEF WEBLIB}
    FOldPosition := nil;
    {$ENDIF}
  end;

begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  case AState of
    gesExportStart:
    begin
      Inc(FInternalCall);

      FDataLink.DataSet.DisableControls;
      FOldPosition := FDataLink.DataSet.GetBookMark;
      DataLink.BufferCount := GetRecordCount;
      if Assigned(Renderer) then
        Renderer.TopRow := Renderer.FixedRowCount;

      DataInstance.RefreshData(True);
    end;
    gesExportDone, gesExportFail:
    begin
      if Assigned(FOldPosition) then
      begin
        if FDataLink.DataSet.BookmarkValid(FOldPosition) then
        begin
          FDataLink.DataSet.GotoBookMark(FOldPosition);
          FDataLink.DataSet.FreeBookMark(FOldPosition);
        end;

        ClearOldPosition;
      end;

      FDataLink.DataSet.EnableControls;
      Dec(FInternalCall);
      DataChanged;
    end;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.FilterMode: TTMSFNCDataGridDataAdapterFilterMode;
begin
  Result := afmNormal;

  if CheckDataSet then
  begin
    case LoadMode of
      almBuffered: Result := afmAdapter;
      almAllRecords: Result := afmNormal;
    end;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetActiveRow: Integer;
begin
  Result := 0;
  if Assigned(Renderer) and CheckDataSet then
  begin
    Result := GetCurrentRecord - 1 + DataInstance.FixedRowCount;
    if Renderer.HasRowOrder[Result] then
      Result := Renderer.RowOrders[Result];
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetBufferCount: Integer;
begin
  if Assigned(Renderer) and (LoadMode = almBuffered) then
  begin
    Result := (Renderer.BottomRow - Renderer.TopRow);
    if Assigned(DetailControl) and (Renderer.RowTypes[Renderer.FixedRowCount] = grtNode) then
      Result := Result div 2;
    
    Result := Result + 1;
  end
  else
    Result := GetRecordCount;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.GetCellProperties(ACell: TTMSFNCDataGridCell);
var
  f: TField;
  o: Integer;
  c, cl: Integer;
  vd: Boolean;
begin
  if not Assigned(ACell) then
    Exit;

  vd := IsValidDataCell(MakeCell(ACell.Column, ACell.Row));

  if not vd then
    Exit;

  cl := ACell.Column;
  c := cl - DataInstance.FixedColumnCount;

  if ACell is TTMSFNCDataGridCheckBoxCell then
  begin
    o := DataLink.ActiveRecord;

    if not SetActiveRecord(ACell.Row) then
      Exit;

    try
      f := FieldAtColumn[c];

      if Assigned(f) then
      begin
        if ShowBooleanFields and (f.DataType = ftBoolean) then
          (ACell as TTMSFNCDataGridCheckBoxCell).Checked := f.AsBoolean;

        if (Columns[c].CheckBoxField) and Assigned(Renderer) and (cl >= 0) and (cl <= Renderer.Columns.Count - 1) then
          (ACell as TTMSFNCDataGridCheckBoxCell).Checked := UpperCase(f.DisplayText) = UpperCase(Renderer.Columns[cl].Binding.BooleanTrueValue);

//      ACell.Text := '';
      end;

    finally
      DataLink.ActiveRecord := o;
    end;
  end;

  if ACell is TTMSFNCDataGridBitmapCell then
  begin
 //     ACell.Text := '';
    ACell.ControlAlign := gcaClient;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetColumn(
  AFieldName: string): TTMSFNCDataGridDatabaseAdapterColumn;
var
  I: Integer;
begin
  Result := nil;
  for I := 0 to Columns.Count - 1 do
  begin
    if Columns[I].FieldName = AFieldName then
    begin
      Result := Columns[I];
      Break;
    end;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetColumnDataName(
  AColumn: Integer): string;
var
  c: Integer;
  f: TField;
begin
  c := AColumn - DataInstance.FixedColumnCount;
  f := FieldAtColumn[c];
  if Assigned(f) then
    Result := f.FieldName;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetCurrentRecord: Integer;
begin
  Result := 1;
  if not CheckDataSet then
    Exit;

  if (DataLink.DataSet.State in [dsInactive, dsBrowse, dsEdit]) then
  begin
    case DataSetType of
      adsSequenced: Result := DataLink.DataSet.RecNo;
      adsNonSequenced: Result := 1;
    end;
  end
  else if Assigned(Renderer) then
    Result := Renderer.FocusedCell.Row - Renderer.FixedRowCount + 1;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.GetCellData(ACell: TTMSFNCDataGridCellCoord;
  var ACellData: TTMSFNCDataGridCellValue);
var
  o, c: Integer;
  m, AField: TField;
  s, ht: String;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  c := ACell.Column - DataInstance.FixedColumnCount;

  if (c >= 0) and (c <= Columns.Count - 1) then
  begin
    if DataLink.DataSet.Active then
    begin
      if (ACell.Row = 0) and (DataInstance.FixedRowCount > 0) then
      begin
        if (Columns[c].Header <> '') then
          ACellData := Columns[c].Header
        else
        begin
          aField := FieldAtColumn[c];
          if Assigned(aField) then
          begin
            if Assigned(aField) and (aField.DisplayLabel <> '') then
              ACellData := aField.DisplayLabel
            else
              ACellData := Columns[c].FieldName;
          end
        end;
      end
      else
      begin
        if not IsValidDataCell(ACell) then
          Exit;

        AField := FieldAtColumn[c];

        if (ShowBooleanFields and (aField <> nil) and (aField.DataType = ftBoolean))
          or (ShowPictureFields and (aField <> nil) and (aField.DataType = ftGraphic))
          or (Columns[c].PictureField) then
          Exit;

        o := DataLink.ActiveRecord;

        if not SetActiveRecord(ACell.Row) then
          Exit;

        try
          if (Columns[c].HTMLTemplate <> '') then
          begin
            ht := Columns[c].HTMLTemplate;

            if Assigned(OnGetHTMLTemplate) then
              OnGetHTMLTemplate(Self, c, ACell.Row, ht, FDataLink.DataSet.Fields);

            ACellData := HTMLDBReplace(ht, FDataLink.DataSet, c, ACell.Row);
          end
          else
          begin
            AField := FieldAtColumn[c];
            if Assigned(AField) then
            begin
              if AField.IsNull then
                ACellData := nil
              else
              begin
                if AField.IsBlob then
                begin
                  case AField.DataType of
                    ftGraphic:
                    begin
                      if Columns[c].PictureField then
                        ACellData := '(GRAPHIC)';
                    end
                    else
                    begin
                      if Assigned(AField.OnGetText) then
                      begin
                        AField.OnGetText(AField, s, True);
                        ACellData := s;
                      end
                      else
                      begin
                        if (AField.DataType in [ftWideMemo, ftMemo]) and not ShowMemoFields then
                          ACellData := '(MEMO)'
                        else
                        begin
                          m := nil;
                          if (AField.FieldKind = fkLookup) then
                            m := AField.DataSet.FieldByName(AField.KeyFields);

                          ConvertFieldToData(ACell, m, AField, ACellData);
                        end;
                      end;
                    end;
                  end;
                end
                else
                begin
                  m := nil;
                  if (AField.FieldKind = fkLookup) then
                    m := AField.DataSet.FieldByName(AField.KeyFields);

                  ConvertFieldToData(ACell, m, AField, ACellData);
                end;
              end;
            end
          end;
        finally
          DataLink.ActiveRecord := o;
        end;
      end;
    end
    else
    begin
      if (ACell.Row = 0) and (DataInstance.FixedRowCount > 0) then
      begin
        if (Columns[c].Header <> '') then
          ACellData := Columns[c].Header
        else
        begin
          if (Columns[c].FieldName <> '') then
            ACellData := Columns[c].FieldName;
        end;
      end;
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.RowExpand(ARow: Integer);
begin
  if not CheckDataSet or not Assigned(Renderer) or IsDesigning then
    Exit;

  if not Assigned(DetailControl) then
    Exit;

  Inc(FInternalCall);
  try
    DetailControl.Visible := True;
    Renderer.Controls[Renderer.FixedColumnCount, ARow + 1] := DetailControl;
  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.RowCollapse(ARow: Integer);
begin
  if not CheckDataSet or not Assigned(Renderer) or IsDesigning then
    Exit;

  if not Assigned(DetailControl) then
    Exit;

  Inc(FInternalCall);
  try
    Renderer.Controls[Renderer.FixedColumnCount, ARow + 1] := nil;
    DetailControl.Visible := False;
  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.RowBeforeExpand(ARow: Integer);
begin
  if not CheckDataSet or not Assigned(Renderer) then
    Exit;

  if not Assigned(DetailControl) then
    Exit;

  Inc(FInternalCall);
  try
    InternalSelectCell(MakeCell(Renderer.FocusedCell.Column, ARow));
    InternalSyncPos(False, True, True);
  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.RowBeforeCollapse(ARow: Integer);
begin
  
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.RowAfterExpand(ARow: Integer);
begin
  if not CheckDataSet or not Assigned(Renderer) and Assigned(DetailControl) then
    Exit;

  if not Assigned(DetailControl) then
    Exit;

  Inc(FInternalCall);
  try
    Renderer.Navigate(MakeCell(Renderer.FocusedCell.Column, ARow + 1));
    FExpanded := True;
  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.RowAfterCollapse(ARow: Integer);
begin
//  DataChanged;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.GetCellDataProperties(
  ACell: TTMSFNCDataGridCellCoord; ACellItem: TTMSFNCDataGridCellItem);
var
  o, c, cl: Integer;
  AField: TField;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  cl := ACell.Column;
  c := cl - DataInstance.FixedColumnCount;

  if (c >= 0) and (c <= Columns.Count - 1) then
  begin
    if not IsValidDataCell(ACell) or not IsExtendedCell(ACellItem) then
      Exit;

    o := DataLink.ActiveRecord;

    if not SetActiveRecord(ACell.Row) then
      Exit;

    try
      AField := FieldAtColumn[c];
      if Assigned(AField) then
      begin
        if ShowBooleanFields and (AField.DataType = ftBoolean) then
        begin
          AsExtendedCell(ACellItem).&Type := gcitCheckBox;
          AsExtendedCell(ACellItem).BooleanValue := AField.Asboolean;
        end;

        if (Columns[c].CheckBoxField) and Assigned(Renderer) and (cl >= 0) and (cl <= Renderer.Columns.Count - 1) then
        begin
          AsExtendedCell(ACellItem).&Type := gcitDataCheckBox;
          AsExtendedCell(ACellItem).BooleanValue := UpperCase(AField.DisplayText) = UpperCase(Renderer.Columns[cl].Binding.BooleanTrueValue);
        end;

        if ShowPictureFields and (AField.DataType = ftGraphic) then
        begin
          BlobFieldToPicture(TBlobField(AField), AsExtendedCell(ACellItem).Bitmap);
          AsExtendedCell(ACellItem).&Type := gcitBitmap;
        end;

        if (Columns[c].PictureField) then
        begin
          BlobFieldToPicture(TBlobField(AField), AsExtendedCell(ACellItem).Bitmap);
          AsExtendedCell(ACellItem).&Type := gcitBitmap;
        end;
      end;
    finally
      DataLink.ActiveRecord := o;
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.GetCellFormatting(
  ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue;
  var AFormatting: TTMSFNCDataGridDataFormatting;
  var AConvertSettings: TFormatSettings; var ACanFormat: Boolean);
var
  c: Integer;
  AField: TField;
  fmt: TTMSFNCDataGridDataFormatting;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  c := ACell.Column - DataInstance.FixedColumnCount;

  if (c >= 0) and (c <= Columns.Count - 1) then
  begin
    if not IsValidDataCell(ACell) then
      Exit;

    AField := FieldAtColumn[c];
    if Assigned(AField) then
    begin
      if ShowDateTimeFields and (AField.DataType in [ftDate, ftTime, ftDateTime, ftTimeStamp, ftTimeStampOffset]) then
      begin
        fmt := AFormatting;
        case AField.DataType of
          ftDate: fmt.&Type := gdftDate;
          ftTime: fmt.&Type := gdftTime;
          ftDateTime, ftTimeStamp, ftTimeStampOffset: fmt.&Type := gdftDateTime;
        end;
        AFormatting := fmt;
      end;
    end;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetDataSource: TDataSource;
begin
  if Assigned(DataLink) then
    Result := DataLink.DataSource
  else
    Result := nil;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetDocURL: string;
begin
  Result := TTMSFNCBaseDocURL + 'tmsfncuipack/components/ttmsfncDataGrid/#databinding';
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetFieldAtColumn(AColumn: Integer): TField;
var
  j: Integer;
begin
  Result := nil;
  j := GetFieldIndexAtColumn(AColumn);
  if (j <> -1) then
    Result := DataLink.DataSet.Fields[j];
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetFieldIndexAtColumn(
  AColumn: Integer): Integer;
var
  f: TField;
begin
  Result := -1;
  if not CheckDataSet or (AColumn >= Columns.Count) then
    Exit;

  if (Columns[AColumn].HTMLTemplate <> '') then
    Exit;

  if Columns[AColumn].FieldName <> '' then
  begin
    f := DataLink.DataSet.FieldByName(Columns[AColumn].FieldName);
    if Assigned(f) then
      Result := f.Index;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetFieldNames(
  AIndex: Integer): string;
begin
  Result := '';
  while AIndex < Columns.Count - 1 do
    Columns.Add;

  Result := Columns[AIndex].FieldName;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.GetFilterData(AColumn: Integer;
  AValues: TStrings);
var
  s: string;
  ds: TDataSet;
  f: TField;
  b, fd, mf: Boolean;
  sl: TStringList;
  c: Integer;
begin
  if not CheckDataset and Assigned(DataInstance) then
    Exit;

  c := AColumn - DataInstance.FixedColumnCount;

  if Assigned(OnGetFilterValues) then
    OnGetFilterValues(Self, c, AValues)
  else
  begin
    f := FieldAtColumn[c];

    if Assigned(f) and (f.FieldKind = fkLookup) then
    begin
      sl := TStringList.Create;
      try
        LoadLookupList(f, sl);
        AValues.AddStrings(sl);
      finally
        sl.Free;
      end;
    end
    else if Assigned(f) and Assigned(f.DataSet) then
    begin
      Inc(FInternalCall);
      f.DataSet.DisableControls;
      mf := not ((DataInstance.Filter.Count > 0) and (DataInstance.Options.Filtering.MultiColumn));

      fd := f.DataSet.Filtered;
      if mf then
        f.DataSet.Filtered := False;

      {$IFDEF SUPPORTCLONING}
      ds := f.DataSet.GetClonedDataSet(True);
      {$ELSE}
      ds := nil;
      {$ENDIF}

      b := False;
      if not Assigned(ds) then
      begin
        ds := f.DataSet;
        b := True;
      end;

      if Assigned(ds) then
      begin
        try
          ds.First;

          while not ds.Eof do
          begin
            s := ds.FieldByName(f.FieldName).AsString;
            AValues.Add(s);
            ds.Next;
          end;

        finally
          if not b then
            ds.Free;

          if mf then
            f.DataSet.Filtered := fd;

          f.DataSet.EnableControls;
          Dec(FInternalCall);
        end;
      end;
    end;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetGridColumnIndex(
  AColumn: Integer): Integer;
begin
  Result := AColumn;
  if Assigned(DataInstance) then
    Result := AColumn + DataInstance.FixedColumnCount;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.GetInplaceEditorProperties(
  ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor;
  AInplaceEditorType: TTMSFNCDataGridInplaceEditorType);
var
  c: Integer;
  lookupCell: Boolean;
  fld: TField;
  sl: TStringList;
begin
  if not CheckDataset or not Assigned(DataInstance) then
  	Exit;

  lookupCell := false;
  c := ACell.Column - DataInstance.FixedColumnCount;

  if (Columns.Count > c) then
  begin
    if IsValidDataCell(ACell) then
    begin
      sl := TStringList.Create;
      try
        fld := FieldAtColumn[c];

        if Assigned(fld) then
          lookupCell := (fld.FieldKind = fkLookup) and (Columns[c].UseLookupEditor);

        if lookupCell then
          LoadLookupList(fld, sl);

        if Assigned(fld) and (AInplaceEditor is TCustomEdit) then
        begin
          if fld.DataType in [ftString, ftWideString, ftWideMemo] then
            TCustomEditOpen(AInplaceEditor as TCustomEdit).MaxLength := fld.Size
          else
            TCustomEditOpen(AInplaceEditor as TCustomEdit).MaxLength := 0;
        end;

        if lookupCell then
        begin
          if AInplaceEditor is TCustomComboBox then
            TCustomComboBoxOpen(AInplaceEditor as TCustomComboBox).Items.Assign(sl);

          {$IFDEF FMXLIB}
          if AInplaceEditor is TCustomComboEdit then
            (AInplaceEditor as TCustomComboEdit).Items.Assign(sl);
          {$ENDIF}
        end;
      finally
        sl.Free;
      end;
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.GetInplaceEditorType(
  ACell: TTMSFNCDataGridCellCoord;
  var AInplaceEditorType: TTMSFNCDataGridInplaceEditorType);
var
  lookupCell: Boolean;
  fld: TField;
  c: Integer;
begin
  lookupCell := false;

  if not CheckDataSet or not Assigned(DataInstance) then
  	Exit;

  c := ACell.Column - DataInstance.FixedColumnCount;

  if (c >= 0) and (c <= Columns.Count - 1) then
  begin
    if Columns[C].UseColumnEditor then
      Exit;

    fld := FieldAtColumn[c];
    if Assigned(fld) then
      lookupCell := (fld.FieldKind = fkLookup) and (Columns[c].UseLookupEditor);

    if Assigned(fld) and not Assigned(fld.OnGetText) then
    begin
      case fld.DataType of
        ftInteger, ftLargeint, ftSmallInt, ftWord: AInplaceEditorType := getNumericEdit;
        ftFloat, ftCurrency: AInplaceEditorType := getFloatEdit;
        ftDate, ftTime, ftDateTime, ftTimeStamp, ftTimeStampOffset: AInplaceEditorType := getDatePicker;
      end;
    end;

    if lookupCell then
      AInplaceEditorType := getComboBox;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetLookupKeys: TTMSFNCDataGridDatabaseAdapterLookupKeys;
begin
  if not Assigned(FLookupKeys) then
    FLookupKeys := TTMSFNCDataGridDatabaseAdapterLookupKeys.Create;

  Result := FLookupKeys;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetRecordCount: Integer;
begin
  Result := 0;
  if not CheckDataSet then
    Exit;

  if DataSource.State = dsSetKey then
    Result := 1
  else
  begin
    Result := DataSource.DataSet.RecordCount;

    if (DataLink.DataSet.State in [dsInsert]) then
      Result := Result + 1;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GetVersion: string;
begin
  Result := GetVersionNumber(MAJ_VER, MIN_VER, REL_VER, BLD_VER);
end;

function TTMSFNCCustomDataGridDatabaseAdapter.GroupMode: TTMSFNCDataGridDataAdapterGroupMode;
begin
  Result := agmNormal;
  if CheckDataSet then
  begin
    case LoadMode of
      almBuffered: Result := agmNone;
      almAllRecords: Result := agmNormal;
    end;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.HTMLDBReplace(AValue: string;
  ADataset: TDataSet; ACol, ARow: Integer): string;
var
  beforetag, aftertag, fld, dbfld: string;
  i, j: integer;
  AField: TField;
begin
  beforetag := '';

  while Pos('<#', AValue) > 0 do
  begin
    i := pos('<#', AValue);
    beforetag := beforetag + copy(AValue, 1, i - 1);
    aftertag := copy(AValue, i, length(AValue));
    j := pos('>', aftertag);
    fld := copy(aftertag, 1, j - 1);
    Delete(fld, 1, 2);
    Delete(AValue, 1, i + j - 1);

    dbfld := '';
    if Assigned(ADataSet) then
    begin
      if ADataSet.Active then
      begin
        AField := ADataSet.FindField(fld);
        if Assigned(AField) then
        begin
          if AField.IsBlob then
          begin
            if not ShowMemoFields then
            begin
              dbfld := '(MEMO)'
            end
            else
            begin
              if Assigned(AField.OnGetText) then
                AField.OnGetText(AField, dbfld, True)
              else
                dbfld := AField.AsString;
            end;
          end
          else
           dbfld := AField.DisplayText;
        end;

        if Assigned(FOnGetHTMLTemplateData) then
          FOnGetHTMLTemplateData(Self, ACol, ARow, fld, dbfld);
      end
      else
        dbfld := '(' + fld + ')';
    end
    else
      dbfld := '(' + fld + ')';

    beforetag := beforetag + dbfld;
  end;

  Result := beforetag + AValue;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.Initialize;
begin
  ActiveChange;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.InsertRow(ARow: Integer);
begin
  if not CheckDataSet or not Assigned(Renderer) then
    Exit;

  Inc(FInternalCall);

  try
    if not (FDataLink.DataSet.State in [dsEdit, dsInsert]) then
    begin
      InternalSelectCell(MakeCell(Renderer.FocusedCell.Column, ARow));
      if ARow = Renderer.RowCount - Renderer.FixedBottomRowCount then
        FDataLink.DataSet.Append
      else
        FDataLink.DataSet.Insert;
    end;
  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.InternalConvertFieldToData(
  AField: TField; var AValue: TTMSFNCDataGridCellValue);
var
  s: string;
begin
  if Assigned(AField.OnGetText) then
  begin
    AField.OnGetText(AField, s, True);
    AValue := s;
  end
  else
  begin
    {$IFDEF WEBLIB}
    if AField.DataType in [ftWord, ftInteger, ftFloat, ftBoolean, ftDate, ftTime, ftString,
      ftWideString, ftLargeInt, ftAutoInc, ftFixedChar, ftVariant, ftMemo, ftWideMemo] then
      AValue := AField.AsJSValue
    else if AField.DataType in [ftDateTime] then
      AValue := AField.AsDateTime
    {$ELSE}
    if AField.DataType in [ftSmallint, ftShortInt, ftByte, ftWord, ftInteger, ftBCD, ftLongWord,
      ftSingle, ftFloat, ftDateTime, ftBoolean, ftCurrency, ftDate, ftTime, ftString,
      ftWideString, ftLargeInt, ftAutoInc, ftFixedChar, ftVariant, ftExtended, ftSingle, ftMemo, ftWideMemo] then
      AValue := TValue.FromVariant(AField.AsVariant)
    else if AField.DataType in [ftTimeStamp, ftTimeStampOffset] then
      AValue := AField.AsDateTime
    else
      AValue := AField.DisplayText;
    {$ENDIF}
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.InternalDataChanged;
var
  rc: Integer;
  I: Integer;
begin
  rc := GetRecordCount;
  DataInstance.BeginUpdate;
  UpdateVisibleFields;

  if Assigned(DetailControl) then
  begin
    DataInstance.ClearData;
    DataInstance.ClearRowInfo;
  end
  else
  begin
    for I := 0 to DataInstance.RowCount - 1 do
    begin
      if DataInstance.IsRowNode(I) or DataInstance.IsRowSummary(I) then
        Inc(rc);
    end;
  end;

  DataInstance.RowCount := rc + DataInstance.FixedRowCount + DataInstance.FixedBottomRowCount;

  LoadDetails;
  DataInstance.EndUpdate;

  CollapseNodes;
  DataLink.BufferCount := GetBufferCount;

  InternalSyncPos(True, True, False);

  DataInstance.RefreshData;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.InternalSelectCell(
  ACell: TTMSFNCDataGridCellCoord);
var
  ci: Boolean;
  l: Integer;
  os: TDataSetState;
  sp: Boolean;
begin
  if (DataInstance.RowReverseOrders[ACell.Row] - DataInstance.FixedRowCount > GetRecordCount - 1) then
    Exit;

  sp := True;

  if (FPrevCell.Row <> ACell.Row) and (ACell.Row <> -1) then
  begin
    try
      if (DataLink.DataSet.State = dsInsert) and not DataLink.DataSet.Modified and not FDatalink.FModified then
      begin
        DataLink.DataSet.Cancel;
        InternalDataChanged;
        sp := False;
      end
      else
      begin
        l := DataInstance.RowReverseOrders[ACell.Row] - DataInstance.RowReverseOrders[FPrevCell.Row];
        ci := (Datalink.DataSet.State = dsInsert) and not FDatalink.Dataset.Modified and not FDatalink.FModified;

        os := DataLink.DataSet.State;

        FDataLink.DataSet.DisableControls;

        if FPrevCell.Row < ACell.Row then
          FDatalink.MoveBy(l - Ord(ci))
        else
          FDatalink.MoveBy(l + Ord(ci and FDatalink.Dataset.Eof));

        SetActiveRecord(ACell.Row);

        if os = dsInsert then
        begin
          InternalDataChanged;
          sp := False;
        end
        else
          InternalSyncPos(False, False, True);

        FDataLink.DataSet.EnableControls;
      end;

      DataInstance.RefreshData;
    finally
    end;
  end;

  if sp then
    FPrevCell := ACell;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.InternalSyncPos(AUpdateTopRow: Boolean; AUpdateSelection: Boolean; ACollapse: Boolean);
var
  r, t: Integer;
begin
  if not CheckDataSet or not Assigned(Renderer) then
    Exit;

  if ACollapse then
    CollapseNodes;

  if AUpdateTopRow then
  begin
    t := SyncTopRow;
    Renderer.TopRow := t;
  end;

  FPrevTopRow := Renderer.TopRow;

  if AUpdateSelection then
  begin
    if LoadMode = almAllRecords then
      r := DataLink.ActiveRecord + DataInstance.FixedRowCount
    else
      r := Renderer.RowReverseOrders[Renderer.TopRow] + DataLink.ActiveRecord;

    if Renderer.HasRowOrder[r] then
      r := Renderer.RowOrders[r];

    Renderer.FocusedCell := MakeCell(Renderer.FocusedCell.Column, r);
  end;

  FPrevCell := Renderer.FocusedCell;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.IsValidDataCell(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if not Assigned(DataInstance) then
    Exit;

  Result := (ACell.Row >= DataInstance.FixedRowCount) and (ACell.Row <= DataInstance.RowCount - 1 - DataInstance.FixedBottomRowCount)
    and (ACell.Column >= DataInstance.FixedColumnCount) and (ACell.Column <= DataInstance.ColumnCount - 1 - DataInstance.FixedRightColumnCount);

  if DataInstance.IsGrouped then
    Result := Result and not DataInstance.IsRowNode(ACell.Row) and not DataInstance.IsRowSummary(ACell.Row)
  else if Assigned(DetailControl) then
    Result := Result and DataInstance.IsRowNode(ACell.Row);
end;

function TTMSFNCCustomDataGridDatabaseAdapter.IsValidDataRow(
  ARow: Integer): Boolean;
begin
  Result := False;
  if not Assigned(DataInstance) then
    Exit;

  Result := (ARow >= DataInstance.FixedRowCount) and (ARow <= DataInstance.RowCount - 1 - DataInstance.FixedBottomRowCount);

  if DataInstance.IsGrouped then
    Result := Result and not DataInstance.IsRowNode(ARow) and not DataInstance.IsRowSummary(ARow)
  else if Assigned(DetailControl) then
    Result := Result and DataInstance.IsRowNode(ARow);
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.LoadAllData;
var
  r, c: Integer;
begin
  if not CheckDataSet or not Assigned(DataInstance) then
    Exit;

  DataInstance.ExportNotification(gesExportStart, -1);
  try
    for r := 0 to DataInstance.RowCount - 1 do
    begin
      DataInstance.ExportNotification(gesExportNextRow, r);
      for c := 0 to DataInstance.ColumnCount - 1 do
        DataInstance.Cells[c, r] := DataInstance.Cells[c, r];
    end;
  finally
    DataInstance.ExportNotification(gesExportDone, -1);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.LoadDetails;
var
  I: Integer;
begin
  if not CheckDataSet or not Assigned(Renderer) then
    Exit;

  if Assigned(DetailControl) then
  begin
    Renderer.BeginUpdate;
    I := Renderer.FixedRowCount;
    while I <= Renderer.RowCount - 1 - Renderer.FixedBottomRowCount do
    begin
      if not Renderer.IsRowNode(I) then
      begin
        if I = Renderer.RowCount - 1 - Renderer.FixedBottomRowCount then
          Renderer.AddRow
        else
          Renderer.InsertRow(I + 1);

        Renderer.SetNodeRow(I, 1, 0, False);
        Renderer.ControlAligns[Renderer.FixedColumnCount, I + 1] := gcaClient;
        Renderer.ReadOnlys[Renderer.FixedColumnCount, I + 1] := True;
        Renderer.MergeCells(Renderer.FixedColumnCount, I + 1, Renderer.ColumnCount - Renderer.FixedColumnCount - Renderer.FixedRightColumnCount, 1);
        Renderer.RowHeights[I + 1] := DetailControl.Height;
      end;
      I := I + 2;
    end;

    Renderer.CollapseAllNodes;
    Renderer.EndUpdate;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.LoadLookupList(AField: TField;
  AList: TStrings);
var
  ds: TDataSet;
  li: TTMSFNCDataGridDatabaseAdapterLookupItem;
  k, s: string;
  rf, kf: TField;
  rfv, kfv: TTMSFNCDataGridCellValue;
  b: Boolean;
begin
  if not CheckDataset then
    Exit;

  if Assigned(AField) and Assigned(AField.LookupDataSet) then
  begin
    {$HINTS OFF}
    {$IF COMPILERVERSION > 31}
    ds := AField.LookupDataSet.GetClonedDataSet(True);
    {$ELSE}
    ds := nil;
    {$IFEND}
    {$HINTS ON}

    b := False;
    if not Assigned(ds) then
    begin
      ds := AField.LookupDataSet;
      b := True;
    end;

    if Assigned(ds) then
    begin
      try
        ds.First;
        LookupKeys.Clear;
        AList.Clear;
        kf := ds.FieldByName(AField.LookupKeyFields);
        rf := ds.FieldByName(AField.LookupResultField);

        if Assigned(rf) and Assigned(kf) then
        begin
          while not ds.Eof do
          begin
            InternalConvertFieldToData(rf, rfv);
            InternalConvertFieldToData(kf, kfv);

            s := TTMSFNCDataGridData.ValueToString(rfv);
            k := TTMSFNCDataGridData.ValueToString(kfv);
            li := TTMSFNCDataGridDatabaseAdapterLookupItem.Create(kfv, rfv);
            AList.AddObject(s, li);
            LookupKeys.AddOrSetValue(s, k);
            ds.Next;
          end;
        end;

      finally
        if not b then
          ds.Free;
      end;
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.LoadValuesFromField(
  AField: TField; AValues: TStrings);
var
  ds: TDataSet;
  s: string;
  f: TField;
  v: TTMSFNCDataGridCellValue;
  b: Boolean;
begin
  if not CheckDataset then
    Exit;

  if Assigned(AField) and Assigned(AField.DataSet) then
  begin
    {$HINTS OFF}
    {$IF COMPILERVERSION > 31}
    ds := AField.DataSet.GetClonedDataSet(True);
    {$ELSE}
    ds := nil;
    {$IFEND}
    {$HINTS ON}

    b := False;
    if not Assigned(ds) then
    begin
      ds := AField.DataSet;
      b := True;
    end;

    if Assigned(ds) then
    begin
      try
        ds.First;
        AValues.Clear;

        f := ds.FieldByName(AField.FieldName);
        if Assigned(f) then
        begin
          while not ds.Eof do
          begin
            InternalConvertFieldToData(f, v);
            s := TTMSFNCDataGridData.ValueToString(v);
            AValues.Add(s);
            ds.Next;
          end;
        end;

      finally
        if not b then
          ds.Free;
      end;
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.MoveColumn(AFromColumn,
  AToColumn: Integer);
var
  c, ct: Integer;
begin
  c := AFromColumn - DataInstance.FixedColumnCount;
  ct := AToColumn - DataInstance.FixedColumnCount;
  if (c >= 0) and (c <= Columns.Count - 1) and (ct >= 0) and (ct <= Columns.Count - 1) then
    Columns[c].Index := ct;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.NeedsItemExtended(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;

  if not CheckDataset then
    Exit;

  Result := True;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.Notification(AComponent: TComponent;
  AOperation: TOperation);
begin
  inherited;
  if (AOperation = opRemove) and (AComponent = DataSource) then
  begin
    DataSource := nil;
    Initialize;
  end;

  if (AOperation = opRemove) and (AComponent = DetailControl) then
  begin
    DetailControl := nil;
    Initialize;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClasses([TTMSFNCCustomDataGridDatabaseAdapter, TTMSFNCDataGridDatabaseAdapterColumn]);
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.ReloadFromDataSet;
begin
  DataChanged;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.RemoveAllColumns;
var
  i: Integer;
begin
  DataInstance.BeginUpdate;
  for i := 0 to Columns.Count - 1 do
  begin
    Columns[i].FieldName := '';
    Columns[i].Header := '';
  end;

  Columns.Clear;
  DataInstance.Clear;
  DataInstance.RowCount := 0;
  DataInstance.ColumnCount := 0;
  DataInstance.EndUpdate;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.RemoveAllFields;
var
  i: Integer;
  flds: Boolean;
begin
  flds := Columns.HasFieldsDefined;
  if (csDesigning in ComponentState) and not flds then
    TTMSFNCUtils.Message(sTMSFNCDataGridDatabaseAdapterNoDataGridAssigned);

  for i := 0 to Columns.Count - 1 do
  begin
    if Columns[i].Header = Columns[i].FieldName then
      Columns[i].Header := '';

    Columns[i].FieldName := '';
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.Scroll;
var
  o, s: Integer;
  sp: Boolean;
begin
  if not CheckDataSet or not Assigned(Renderer) then
    Exit;

  if (FInternalCall > 0) or (LoadMode = almAllRecords) or (DataInstance.RowReverseOrders[Renderer.TopRow] - DataInstance.FixedRowCount > GetRecordCount - 1) then
    Exit;

  Inc(FInternalCall);
  try
    sp := True;

    if (FPrevTopRow <> Renderer.TopRow) then
    begin
      o := DataInstance.RowReverseOrders[Renderer.TopRow] - DataInstance.RowReverseOrders[FPrevTopRow];

      if (DataLink.DataSet.State = dsInsert) and not DataLink.DataSet.Modified and not FDatalink.FModified then
      begin
        DataLink.DataSet.Cancel;
        InternalDataChanged;
        sp := False;
      end
      else
      begin
        FDataLink.DataSet.DisableControls;

        if o > 0 then
        begin
          s := FDataLink.BufferCount - FDataLink.ActiveRecord - 1;
          if s > 0 then
            FDataLink.MoveBy(s);
        end
        else if o < 0 then
        begin
          s := -FDataLink.ActiveRecord;
          if s < 0 then
            FDataLink.MoveBy(s);
        end;

        FDataLink.MoveBy(o);

        InternalSyncPos(True, True, True);

        FDataLink.DataSet.EnableControls;
      end;

      Renderer.RefreshData;
    end;

    if sp then
      FPrevTopRow := Renderer.TopRow;

  finally
    Dec(FInternalCall);
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SelectCell(
  ACell: TTMSFNCDataGridCellCoord);
begin
  if not CheckDataSet or not Assigned(Renderer) then
    Exit;

  if (FInternalCall > 0) then
    Exit;

  Inc(FInternalCall);
  try
    InternalSelectCell(ACell);
  finally
    Dec(FInternalCall);
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.SetActiveRecord(ARow: Integer): Boolean;
var
  o: Integer;
begin
  o := DataLink.ActiveRecord;

  if Assigned(Renderer) and (LoadMode = almBuffered) then
    DataLink.ActiveRecord := DataInstance.RowReverseOrders[ARow] - DataInstance.RowReverseOrders[Renderer.TopRow]
  else
    DataLink.ActiveRecord := DataInstance.RowReverseOrders[ARow] - DataInstance.FixedRowCount;

  if (DataLink.ActiveRecord < 0) or ((DataLink.ActiveRecord >= DataLink.BufferCount) and not ((DataLink.ActiveRecord = DataLink.BufferCount) and (DataSetType = adsNonSequenced))) then
  begin
    DataLink.ActiveRecord := o;
    Result := False;
  end
  else
    Result := True;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetCellData(
  ACell: TTMSFNCDataGridCellCoord; ACellData: TTMSFNCDataGridCellValue);
begin
  if not CheckDataset then
    Exit;

  try
    if not FIsEditing and DataLink.Editing then
      EditSetData(ACell, nil, ACellData);
  finally
    FIsEditing := False;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetColumns(
  const Value: TTMSFNCDataGridDatabaseAdapterColumns);
begin
  FColumns.Assign(Value);
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetDataSetType(
  const Value: TTMSFNCDataGridDatabaseAdapterDataSetType);
begin
  if Value <> FDataSetType then
  begin
    FDataSetType := Value;
    if CheckDataSet then
    begin
      DataLink.DataSet.Active := false;
      DataLink.DataSet.Active := true;
    end;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetDetailControl(
  const Value: TTMSFNCDataGridCellControl);
begin
  if Value = nil then
  begin
    if Assigned(FDetailControl) then
      FDetailControl.Visible := True;
  end;

  FDetailControl := Value;
  if Assigned(FDetailControl) and not IsDesigning then
    FDetailControl.Visible := False;

  Initialize;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetFieldNames(AIndex: Integer;
  const Value: string);
begin
  while AIndex > Columns.Count - 1 do
    Columns.Add;

  Columns[AIndex].FieldName := Value;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetDataSource(const Value: TDataSource);
begin
  if Value = DataLink.Datasource then
    Exit;

  DataLink.DataSource := Value;

  if not Assigned(DataLink.DataSource) then
  begin
    DataInstance.BeginUpdate;
    DataInstance.Clear;
    DataInstance.RowCount := DataInstance.FixedRowCount + DataInstance.FixedBottomRowCount + 1;

    if AutoRemoveColumns then
      DataInstance.ColumnCount := 0;

    DataInstance.EndUpdate;
  end;

  if Value <> nil then
    Value.FreeNotification(Self);
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetLoadMode(
  const Value: TTMSFNCDataGridDatabaseAdapterLoadMode);
begin
  if FLoadMode <> Value then
  begin
    FLoadMode := Value;
    ActiveChange;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetShowBooleanFields(
  const Value: Boolean);
begin
  if FShowBooleanFields <> Value then
  begin
    FShowBooleanFields := Value;
    ActiveChange;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetShowDateTimeFields(
  const Value: Boolean);
begin
  if FShowDateTimeFields <> Value then
  begin
    FShowDateTimeFields := Value;
    ActiveChange;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetShowMemoFields(
  const Value: Boolean);
begin
  if FShowMemoFields <> Value then
  begin
    FShowMemoFields := Value;
    ActiveChange;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SetShowPictureFields(
  const Value: Boolean);
begin
  if FShowPictureFields <> Value then
  begin
    FShowPictureFields := Value;
    ActiveChange;
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.SortData;
var
  c: Integer;
  f: TField;
  {$IFDEF SUPPORTCDS}
  cds: TCustomClientDataSetOpen;
  idf: TIndexDef;
  n: string;
  {$ENDIF}
  {$IFDEF SUPPORTFIREDAC}
  fds: TFDDataSet;
  fdidx: TFDIndex;
  {$ENDIF}
  {$IFDEF SUPPORTABSTRACT}
  ads: TTMSFNCCustomDataSet;
  fn: string;
  {$ENDIF}
  s: TTMSFNCDataGridSortIndex;
begin
  if CheckDataSet and Assigned(DataLink) then
  begin
    Inc(FInternalCall);
    DataLink.DataSet.DisableControls;
    try
      if Assigned(OnSortData) then
        OnSortData(Self)
      else
      begin
        {$IFDEF SUPPORTCDS}
        if DataLink.DataSet is TCustomClientDataSet then
        begin
          cds := TCustomClientDataSetOpen(DataLink.DataSet);
          cds.IndexDefs.Clear;
          cds.IndexName := '';

          for s in DataInstance.SortIndexList do
          begin
            if s.Direction <> gsdNone then
            begin
              c := s.Column - DataInstance.FixedColumnCount;
              f := FieldAtColumn[c];
              if Assigned(f) and (f.FieldKind in [fkData, fkInternalCalc, fkLookup]) then
              begin
                idf := cds.IndexDefs.AddIndexDef;
                if f.FieldKind = fkLookup then
                  idf.Fields := f.KeyFields
                else
                  idf.Fields := f.FieldName;

                n := 'IDX_' + f.FieldName;

                if s.Direction = gsdDescending then
                begin
                  n := n + '_DESC';
                  idf.Options := [ixDescending];
                end
                else
                  n := n + '_ASC';

                idf.Name := n;
                cds.IndexName := n;

                Break;
              end;
            end;
          end;
        end;
        {$ENDIF}
        {$IFDEF SUPPORTFIREDAC}
        if DataLink.DataSet is TFDDataSet then
        begin
          fds := DataLink.DataSet as TFDDataSet;
          fds.Indexes.Clear;

          for s in DataInstance.SortIndexList do
          begin
            if s.Direction <> gsdNone then
            begin
              c := s.Column - DataInstance.FixedColumnCount;
              f := FieldAtColumn[c];
              if Assigned(f) and (f.FieldKind in [fkData, fkInternalCalc, fkLookup]) then
              begin
                fdidx := fds.Indexes.Add;
                if f.FieldKind = fkLookup then
                  fdidx.Fields := f.KeyFields
                else
                  fdidx.Fields := f.FieldName;

                n := 'IDX_' + f.FieldName;

                if s.Direction = gsdDescending then
                begin
                  n := n + '_DESC';
                  fdidx.Options := [soDescending];
                end
                else
                  n := n + '_ASC';

                fdidx.Name := n;
                fdidx.Active := True;

                fds.IndexName := n;
                Break;
              end;
            end;
          end;
        end;
        {$ENDIF}

        {$IFDEF SUPPORTABSTRACT}
        if DataLink.DataSet is TTMSFNCCustomDataSet then
        begin
          ads := DataLink.DataSet as TTMSFNCCustomDataSet;
          ads.UnSort;
          for s in DataInstance.SortIndexList do
          begin
            if s.Direction <> gsdNone then
            begin
              c := s.Column - DataInstance.FixedColumnCount;
              f := FieldAtColumn[c];
              if Assigned(f) and (f.FieldKind in [fkData, fkInternalCalc, fkLookup]) then
              begin
                if f.FieldKind = fkLookup then
                  fn := f.KeyFields
                else
                  fn := f.FieldName;

                case s.Direction of
                  gsdAscending:
                  begin
                    ads.SortBy(fn, dsdAscending);
                    Break;
                  end;
                  gsdDescending:
                  begin
                    ads.SortBy(fn, dsdDescending);
                    Break;
                  end;
                end;
              end;
            end;
          end;
        end;
        {$ENDIF}
      end;
    finally
      InternalDataChanged;
      DataLink.DataSet.EnableControls;
      Dec(FInternalCall);
    end;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.SortMode: TTMSFNCDataGridDataAdapterSortMode;
begin
  Result := asmNormal;
  if CheckDataSet then
  begin
    case LoadMode of
      almBuffered: Result := asmAdapter;
      almAllRecords: Result := asmNormal;
    end;
  end;
end;

function TTMSFNCCustomDataGridDatabaseAdapter.SyncTopRow: Integer;
begin
  Result := 0;
  if Assigned(Renderer) and CheckDataSet then
  begin
    Result := GetCurrentRecord - 1 + DataInstance.FixedRowCount - DataLink.ActiveRecord;
    if Renderer.HasRowOrder[Result] then
      Result := Renderer.RowOrders[Result];
  end;
end;

procedure TTMSFNCCustomDataGridDatabaseAdapter.UpdateVisibleFields;
var
  i, c: integer;
  f: TField;
  fv: Boolean;
  gc: TTMSFNCDataGridColumn;
begin
  DataInstance.BeginUpdate;
  DataInstance.ColumnCount := Columns.Count + DataInstance.FixedColumnCount + DataInstance.FixedRightColumnCount;

  for i := 0 to Columns.Count - 1 do
  begin
    f := FieldAtColumn[i];

    if Assigned(f) then
    begin
      fv := not f.Visible;

      gc := Columns[i].GridColumn;
      if Assigned(gc) then
      begin
        fv := fv or not gc.Visible;
        c := gc.Index;
      end
      else
        c := i;

      if fv then
        DataInstance.HideColumn(c)
      else
        DataInstance.UnHideColumn(c);

      DataInstance.ClearDataForColumn(c, [grtDefault]);

      if Assigned(gc) then
      begin
        if (f.DisplayWidth > 0) and Columns[i].FNew then
        begin
          gc.Width := TextWidthToColWidth(gc.Index, DataInstance, f.DisplayWidth);
          Columns[i].FNew := False;
        end;
      end;
    end;
  end;

  DataInstance.RefreshData;
  DataInstance.EndUpdate;
end;

{ TTMSFNCDataGridDatabaseAdapterDataLink }

function TTMSFNCDataGridDatabaseAdapterDataLink.Adapter: TTMSFNCDataGridDataAdapter;
begin
  Result := FAdapter;
end;

constructor TTMSFNCDataGridDatabaseAdapterDataLink.Create(AAdapter: TTMSFNCCustomDataGridDatabaseAdapter);
begin
  inherited Create;
  FAdapter := AAdapter;
  FModified := False;
  VisualControl := True;
end;

procedure TTMSFNCDataGridDatabaseAdapterDataLink.ActiveChanged;
begin
  inherited;
  if Active and Assigned(DataSource) then
    if Assigned(DataSource.DataSet) then
      if DataSource.DataSet.IsUnidirectional then
        DatabaseError(sTMSFNCDataGridDatabaseAdapterUnidirectionalDataSet);
  if Assigned(FAdapter) then
    FAdapter.ActiveChange;
  FModified := False;
end;

procedure TTMSFNCDataGridDatabaseAdapterDataLink.DataSetChanged;
begin
  inherited;
  if Assigned(FAdapter) then
    FAdapter.DataChanged;
  FModified := False;
end;

procedure TTMSFNCDataGridDatabaseAdapterDataLink.DataSetScrolled(Distance: Integer);
begin
  inherited;
  if Assigned(FAdapter) then
    FAdapter.DataSetScrolled;
end;

destructor TTMSFNCDataGridDatabaseAdapterDataLink.Destroy;
begin

  inherited;
end;

procedure TTMSFNCDataGridDatabaseAdapterDataLink.LayoutChanged;
begin
  inherited;
  if Assigned(FAdapter) then
    FAdapter.DataChanged;
  FModified := False;
end;

procedure TTMSFNCDataGridDatabaseAdapterDataLink.RecordChanged(Field: TField);
begin
  inherited;
  if Assigned(FAdapter) and not (FAdapter.FInternalCall > 0) then
  begin
    FAdapter.UpdateData;
    FModified := False;
  end;
end;

procedure TTMSFNCDataGridDatabaseAdapterDataLink.SetModified(const Value: Boolean);
begin
  FModified := FModified or Value;
end;

procedure TTMSFNCDataGridDatabaseAdapterDataLink.UpdateData;
begin
  if FModified and Assigned(FAdapter) then
    FAdapter.UpdateData;
  FModified := False;
end;

{ TTMSFNCDataGridDatabaseAdapterColumn }

function TTMSFNCDataGridDatabaseAdapterColumn.Adapter: TTMSFNCCustomDataGridDatabaseAdapter;
begin
  Result := FAdapter;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDatabaseAdapterColumn then
  begin
    FFieldName := (Source as TTMSFNCDataGridDatabaseAdapterColumn).FFieldName;
    FHeader := (Source as TTMSFNCDataGridDatabaseAdapterColumn).Header;
    FCheckBoxField := (Source as TTMSFNCDataGridDatabaseAdapterColumn).CheckBoxField;
    FProgressField := (Source as TTMSFNCDataGridDatabaseAdapterColumn).ProgressField;
    FPictureField := (Source as TTMSFNCDataGridDatabaseAdapterColumn).PictureField;
    FUseLookupEditor := (Source as TTMSFNCDataGridDatabaseAdapterColumn).UseLookupEditor;
    FUseColumnEditor := (Source as TTMSFNCDataGridDatabaseAdapterColumn).UseColumnEditor;
    FHTMLTemplate := (Source as TTMSFNCDataGridDatabaseAdapterColumn).HTMLTemplate;
  end;
end;

constructor TTMSFNCDataGridDatabaseAdapterColumn.Create(ACollection: TCollection);
begin
  inherited;
  FNew := False;
  FAutoCreated := False;
  FUseLookupEditor := True;
  FCheckBoxField := False;
  FProgressField := False;
  FPictureField := False;
  FAdapter := nil;
  if Assigned(Collection) and (Collection is TTMSFNCDataGridDatabaseAdapterColumns) then
    FAdapter := (Collection as TTMSFNCDataGridDatabaseAdapterColumns).Adapter;

  if Assigned(FAdapter) and Assigned(FAdapter.DataInstance) then
  begin
    if Assigned(Collection) then
    begin
      while Collection.Count > FAdapter.DataInstance.ColumnCount - FAdapter.DataInstance.FixedColumnCount do
      begin
        FAdapter.DataInstance.AddColumn;
        FAdapter.DataInstance.ClearDataForColumn(FAdapter.DataInstance.ColumnCount - 1,  [grtDefault]);
        FAdapter.DataInstance.RefreshData();
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.ReadAutoCreated(Reader: TReader);
begin
  AutoCreated := Reader.ReadBoolean;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.WriteAutoCreated(Writer: TWriter);
begin
  Writer.WriteBoolean(AutoCreated);
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.DefineProperties(Filer: TFiler);
begin
  inherited;
  {$IFDEF WEBLIB}
  Filer.DefineProperty('AutoCreated', @ReadAutoCreated, @WriteAutoCreated, True);
  {$ENDIF}
  {$IFNDEF WEBLIB}
  Filer.DefineProperty('AutoCreated', ReadAutoCreated, WriteAutoCreated, True);
  {$ENDIF}
end;

destructor TTMSFNCDataGridDatabaseAdapterColumn.Destroy;
var
  idx: Integer;
begin
  idx := Index;
  inherited;
  if Assigned(FAdapter) and not FAdapter.IsDestroying and Assigned(FAdapter.DataInstance) and (idx >= 0) and (idx <= FAdapter.DataInstance.ColumnCount - 1) then
  begin
    FAdapter.DataInstance.ClearDataForColumn(idx,  [grtDefault]);
    FAdapter.DataInstance.DeleteColumn(idx);
    FAdapter.DataInstance.RefreshData();
  end;
end;

function TTMSFNCDataGridDatabaseAdapterColumn.GetDisplayName: string;
begin
  if Header = '' then
  begin
    if FieldName = '' then
      Result := 'Column ' + Inttostr(Index)
    else
      Result := FieldName;
  end
  else
    Result := Header;
end;

function TTMSFNCDataGridDatabaseAdapterColumn.GetField: TField;
var
  ap: TTMSFNCCustomDataGridDatabaseAdapter;
begin
  ap := Adapter;
  if (FField = nil) and (Length(FFieldName) > 0) and Assigned(ap) and
    Assigned(ap.DataLink.DataSet) then
    with ap.Datalink.DataSet do
      if Active {$IFNDEF WEBLIB}or not (lcAutomatic in Fields.LifeCycles){$ENDIF} then
        SetField(FindField(FieldName));
  Result := FField;
end;

function TTMSFNCDataGridDatabaseAdapterColumn.GetGridColumn: TTMSFNCDataGridColumn;
var
  r: TTMSFNCDataGridRenderer;
  c: Integer;
begin
  Result := nil;
  if Assigned(FAdapter) and Assigned(FAdapter.DataInstance) and (FAdapter.DataInstance is TTMSFNCDataGridRenderer) then
  begin
    r := (FAdapter.DataInstance as TTMSFNCDataGridRenderer);

    c := Index + r.FixedColumnCount;

    if (c >= 0) and (c <= r.Columns.Count - 1) then
      Result := r.Columns[c];
  end;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.SetCheckBoxField(
  const Value: Boolean);
begin
  if Value and (FPictureField or FProgressField) then
    raise Exception.Create(sTMSFNCDataGridDatabaseAdapterPictureField);

  if FCheckBoxField <> Value then
  begin
    FCheckBoxField := Value;

    if Assigned(FAdapter) then
      FAdapter.UpdateData;
  end;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.SetField(Value: TField);
var
  ap: TTMSFNCCustomDataGridDatabaseAdapter;
begin
  if FField = Value then
    Exit;

  if Assigned(Value) and (csDestroying in Value.ComponentState) then
    Value := nil;

  ap := Adapter;

  if Assigned(FField) and Assigned(ap) then
    FField.RemoveFreeNotification(ap);

  FField := Value;
  if Assigned(Value) then
  begin
    if Assigned(ap) then
      FField.FreeNotification(ap);
    FFieldName := Value.FieldName;
  end;

  if Assigned(FAdapter) then
    FAdapter.UpdateData;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.SetFieldName(const Value: string);
var
  f: TField;
  ap: TTMSFNCCustomDataGridDatabaseAdapter;
begin
  f := nil;
  ap := Adapter;
  if Assigned(ap) and Assigned(ap.DataLink.DataSet) and
    not (csLoading in ap.ComponentState) and (Length(Value) > 0) then
      f := ap.DataLink.DataSet.FindField(Value);
  FFieldName := Value;
  if Value <> '' then
    Field := f;

  if Assigned(FAdapter) then
    FAdapter.UpdateData;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.SetHeader(const Value: string);
var
  ap: TTMSFNCCustomDataGridDatabaseAdapter;
begin
  FHeader := Value;
  ap := Adapter;
  if Assigned(ap) and Assigned(ap.DataInstance) then
    ap.DataInstance.Cells[Index + ap.DataInstance.FixedColumnCount, 0] := FHeader;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.SetHTMLTemplate(const Value: string);
begin
  FHTMLTemplate := Value;
  if assigned(FAdapter) then
    FAdapter.UpdateData;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.SetIndex(Value: Integer);
var
  og: Integer;
begin
  og := Index;
  inherited;
  if Assigned(FAdapter) and Assigned(FAdapter.DataInstance) then
  begin
    FAdapter.DataInstance.MoveColumn(og, Index);
    FAdapter.DataInstance.ClearDataForColumn(og, [grtDefault]);
    FAdapter.DataInstance.ClearDataForColumn(Index, [grtDefault]);
    FAdapter.DataInstance.RefreshData();
  end;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.SetPictureField(
  const Value: Boolean);
begin
  if Value and (FCheckBoxField or FProgressField) then
    raise Exception.Create(sTMSFNCDataGridDatabaseAdapterCheckBoxField);

  if FPictureField <> Value then
  begin
    FPictureField := Value;
    if Assigned(FAdapter) then
      FAdapter.UpdateData;
  end;
end;

procedure TTMSFNCDataGridDatabaseAdapterColumn.SetProgressField(
  const Value: Boolean);
begin
  if Value and (FCheckBoxField or FPictureField) then
    raise Exception.Create(sTMSFNCDataGridDatabaseAdapterCheckPictureField);

  if FProgressField <> Value then
  begin
    FProgressField := Value;
    if Assigned(FAdapter) then
      FAdapter.UpdateData;
  end;
end;

{ TTMSFNCDataGridDatabaseAdapterColumns }

function TTMSFNCDataGridDatabaseAdapterColumns.Adapter: TTMSFNCCustomDataGridDatabaseAdapter;
begin
  Result := FAdapter;
end;

function TTMSFNCDataGridDatabaseAdapterColumns.Add: TTMSFNCDataGridDatabaseAdapterColumn;
begin
  Result := TTMSFNCDataGridDatabaseAdapterColumn(inherited Add);
end;

constructor TTMSFNCDataGridDatabaseAdapterColumns.Create(AAdapter: TTMSFNCCustomDataGridDatabaseAdapter);
begin
  inherited Create(AAdapter, TTMSFNCDataGridDatabaseAdapterColumn);
  FAdapter := AAdapter;
end;

function TTMSFNCDataGridDatabaseAdapterColumns.GetItem(Index: Integer): TTMSFNCDataGridDatabaseAdapterColumn;
begin
  Result := TTMSFNCDataGridDatabaseAdapterColumn(inherited GetItem(Index));
end;

function TTMSFNCDataGridDatabaseAdapterColumns.HasFieldsDefined: boolean;
var
  i: Integer;
begin
  Result := False;
  for i := 0 to Count - 1 do
  begin
    if (Items[i].FieldName <> '') and not (Items[i].AutoCreated) then
    begin
      Result := True;
      Break;
    end;
  end;
end;

function TTMSFNCDataGridDatabaseAdapterColumns.Insert(index: Integer): TTMSFNCDataGridDatabaseAdapterColumn;
begin
  Result := TTMSFNCDataGridDatabaseAdapterColumn(inherited Insert(Index));
end;

procedure TTMSFNCDataGridDatabaseAdapterColumns.SetItem(Index: Integer; const Value: TTMSFNCDataGridDatabaseAdapterColumn);
begin
  inherited SetItem(Index, Value);
end;

{ TTMSFNCDataGridDatabaseAdapter }

constructor TTMSFNCDataGridDatabaseAdapter.Create(AOwner: TComponent; ASkipAssign: Boolean);
var
  I: Integer;
  g: TTMSFNCCustomDataGrid;
begin
  inherited Create(AOwner);

  if IsDesignTime and Assigned(AOwner) and (AOwner is TCustomForm) and not ASkipAssign then
  begin
    for I := 0 to AOwner.ComponentCount - 1 do
    begin
      if (AOwner.Components[i] is TTMSFNCCustomDataGrid) then
      begin
        g := AOwner.Components[i] as TTMSFNCCustomDataGrid;
        if not Assigned(TTMSFNCCustomDataGridOpen(g).Adapter) then
          TTMSFNCCustomDataGridOpen(g).Adapter := Self;

        Break;
      end;
    end;
  end;
end;

constructor TTMSFNCDataGridDatabaseAdapter.Create(AOwner: TComponent);
begin
  Create(AOwner, False);
end;

{ TTMSFNCDataGridDatabaseAdapterLookupItem }

constructor TTMSFNCDataGridDatabaseAdapterLookupItem.Create(AKey,
  AValue: TTMSFNCDataGridCellValue);
begin
  FKey := AKey;
  FValue := AValue;
end;

end.
